import React, { useState, useMemo, useEffect } from 'react';
import { Button, CircularProgress, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import axios from 'axios';

// Yantra brand mark, served from /public/yantra_icon.svg.
// Replaces the generic 🤖 emoji used previously for Yantra Intelligence features.
const YantraIcon = ({ size = 16, style = {} }) => (
    <img src="/yantra_icon.svg" alt="Yantra"
        width={size} height={size}
        style={{ display: 'inline-block', verticalAlign: 'middle', ...style }} />
);

// ===== Line-level LCS diff (no external deps) =====
// Returns an ordered array of segments: { type: '=' | '-' | '+', text, aIdx, bIdx }
// Capped at 5000 lines per side; beyond that we show a truncation note.
const MAX_DIFF_LINES = 5000;
function diffLines(a, b) {
    const Araw = (a || '').split('\n');
    const Braw = (b || '').split('\n');
    const truncated = Araw.length > MAX_DIFF_LINES || Braw.length > MAX_DIFF_LINES;
    const A = Araw.slice(0, MAX_DIFF_LINES);
    const B = Braw.slice(0, MAX_DIFF_LINES);
    const m = A.length, n = B.length;
    // LCS length table
    const dp = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
    for (let i = 0; i < m; i++) {
        for (let j = 0; j < n; j++) {
            dp[i + 1][j + 1] = A[i] === B[j]
                ? dp[i][j] + 1
                : Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
    }
    // Backtrack
    const out = [];
    let i = m, j = n;
    while (i > 0 && j > 0) {
        if (A[i - 1] === B[j - 1]) { out.push({ type: '=', text: A[i - 1], aIdx: i - 1, bIdx: j - 1 }); i--; j--; }
        else if (dp[i - 1][j] >= dp[i][j - 1]) { out.push({ type: '-', text: A[i - 1], aIdx: i - 1, bIdx: null }); i--; }
        else { out.push({ type: '+', text: B[j - 1], aIdx: null, bIdx: j - 1 }); j--; }
    }
    while (i > 0) { i--; out.push({ type: '-', text: A[i], aIdx: i, bIdx: null }); }
    while (j > 0) { j--; out.push({ type: '+', text: B[j], aIdx: null, bIdx: j }); }
    out.reverse();
    const adds = out.filter(s => s.type === '+').length;
    const dels = out.filter(s => s.type === '-').length;
    return { segments: out, adds, dels, truncated };
}

// Side-by-side render: aligns matching lines, blanks for inserts on one side
function buildSideBySide(segments) {
    // Walk segments, pair '-' followed by '+' as a modify, else blank-pad
    const rows = [];
    let i = 0;
    while (i < segments.length) {
        const s = segments[i];
        if (s.type === '=') {
            rows.push({ left: s, right: s, kind: 'same' });
            i++;
        } else if (s.type === '-' && segments[i + 1]?.type === '+') {
            rows.push({ left: s, right: segments[i + 1], kind: 'mod' });
            i += 2;
        } else if (s.type === '-') {
            rows.push({ left: s, right: null, kind: 'del' });
            i++;
        } else { // '+'
            rows.push({ left: null, right: s, kind: 'add' });
            i++;
        }
    }
    return rows;
}

const DIFF_COLORS = {
    sameBg: '#fff',
    addBg: '#dcfce7',
    delBg: '#fee2e2',
    modBg: '#fef3c7',
    addText: '#15803d',
    delText: '#b91c1c',
    gutter: '#9ca3af',
    border: '#e5e7eb',
};

// ===== G-code line parser =====
// Extracts the meaningful tokens from a single line.
// Handles common Fanuc/Siemens forms: G0G90X-80.0Y0.0S1100M3G43Z80, M8, N4(comment), etc.
function parseGcodeLine(raw) {
    const text = (raw || '').trim();
    if (!text) return { kind: 'blank', raw: '' };
    // Inline parens-comment (e.g. "(FEED 90→200)")
    const commentMatch = text.match(/\(([^)]+)\)/);
    const comment = commentMatch ? commentMatch[1].trim() : null;
    const stripped = text.replace(/\(([^)]+)\)/g, '').trim();
    // Pure comment line
    if (!stripped) return { kind: 'comment', raw: text, comment };
    // Extract single-letter+number tokens (G0, X-80.0, F200, etc.)
    const tokens = stripped.match(/([A-Z])(-?\d+\.?\d*)/g) || [];
    const out = { kind: 'cmd', raw: text, comment, cmds: [], coords: {}, feeds: null, spindle: null, tool: null, mcodes: [], gcodes: [], hcodes: [], blockNum: null };
    for (const tok of tokens) {
        const letter = tok[0];
        const val = tok.slice(1);
        if (letter === 'N') { out.blockNum = Number(val); continue; }
        if (letter === 'G') { out.gcodes.push(`G${val}`); out.cmds.push(`G${val}`); continue; }
        if (letter === 'M') { out.mcodes.push(`M${val}`); continue; }
        if (letter === 'T') { out.tool = Number(val); continue; }
        if (letter === 'F') { out.feeds = Number(val); continue; }
        if (letter === 'S') { out.spindle = Number(val); continue; }
        if (letter === 'H') { out.hcodes.push(`H${val}`); continue; }
        if (['X', 'Y', 'Z', 'I', 'J', 'K', 'R', 'Q', 'P', 'D'].includes(letter)) {
            out.coords[letter] = Number(val);
        }
    }
    // Is this a motion line?
    out.isMotion = out.gcodes.some(g => /^G0?[0-3]$/.test(g)) || (Object.keys(out.coords).some(k => 'XYZ'.includes(k)) && out.gcodes.length === 0);
    return out;
}

// Compare left vs right parsed lines and produce a list of human-readable change notes
function explainChange(leftRaw, rightRaw, kind) {
    const L = leftRaw ? parseGcodeLine(leftRaw) : null;
    const R = rightRaw ? parseGcodeLine(rightRaw) : null;
    const notes = [];
    let headline = '';
    let svg = null;

    if (kind === 'add') {
        headline = 'Line added by Yantra Intelligence';
        if (R?.comment) notes.push({ icon: '💬', text: `Yantra Intelligence annotation: "${R.comment}"` });
        if (R?.mcodes?.length) notes.push({ icon: '⚙️', text: `Adds M-code: ${R.mcodes.join(', ')}` });
        if (R?.isMotion) notes.push({ icon: '➡️', text: `Inserts motion: ${Object.entries(R.coords).map(([k, v]) => `${k}${v}`).join(' ')}` });
        if (!notes.length) notes.push({ icon: '➕', text: 'Inserted line — see code on right' });
        return { headline, notes, svg };
    }
    if (kind === 'del') {
        headline = 'Line removed by Yantra Intelligence';
        if (L?.mcodes?.length) notes.push({ icon: '⚙️', text: `Removes M-code: ${L.mcodes.join(', ')} (likely merged into adjacent line)` });
        if (L?.isMotion) notes.push({ icon: '➖', text: `Removes redundant motion: ${Object.entries(L.coords).map(([k, v]) => `${k}${v}`).join(' ')}` });
        if (!notes.length) notes.push({ icon: '➖', text: 'Removed line — no longer needed' });
        return { headline, notes, svg };
    }
    // kind === 'mod' — compare both
    headline = 'Line modified by Yantra Intelligence';
    if (R?.comment && (!L?.comment || L.comment !== R.comment)) {
        notes.push({ icon: '💬', text: `Yantra Intelligence annotation: "${R.comment}"` });
    }
    // Feed rate change
    if (L?.feeds !== R?.feeds && (L?.feeds || R?.feeds)) {
        const dir = (R?.feeds || 0) > (L?.feeds || 0) ? '▲ faster' : '▼ slower';
        notes.push({ icon: '⚡', text: `Feed rate ${L?.feeds ?? '—'} → ${R?.feeds ?? '—'} (${dir})` });
    }
    // Spindle change
    if (L?.spindle !== R?.spindle && (L?.spindle || R?.spindle)) {
        notes.push({ icon: '🌀', text: `Spindle ${L?.spindle ?? '—'} → ${R?.spindle ?? '—'} RPM` });
    }
    // Tool change
    if (L?.tool !== R?.tool && (L?.tool || R?.tool)) {
        notes.push({ icon: '🔧', text: `Tool T${L?.tool ?? '—'} → T${R?.tool ?? '—'}` });
    }
    // M-codes added/removed inline
    const lM = new Set(L?.mcodes || []);
    const rM = new Set(R?.mcodes || []);
    const addedM = [...rM].filter(m => !lM.has(m));
    const removedM = [...lM].filter(m => !rM.has(m));
    if (addedM.length) notes.push({ icon: '➕', text: `M-codes inlined: ${addedM.join(', ')}` });
    if (removedM.length) notes.push({ icon: '➖', text: `M-codes removed: ${removedM.join(', ')}` });
    // Coord changes
    const allCoords = new Set([...Object.keys(L?.coords || {}), ...Object.keys(R?.coords || {})]);
    const coordChanges = [];
    for (const k of allCoords) {
        const lv = L?.coords?.[k];
        const rv = R?.coords?.[k];
        if (lv !== rv) coordChanges.push(`${k}: ${lv ?? '—'} → ${rv ?? '—'}`);
    }
    if (coordChanges.length) notes.push({ icon: '📐', text: `Coordinates: ${coordChanges.join(', ')}` });
    // G-codes added (e.g. G43 tool length compensation merged in)
    const lG = new Set(L?.gcodes || []);
    const rG = new Set(R?.gcodes || []);
    const addedG = [...rG].filter(g => !lG.has(g));
    const removedG = [...lG].filter(g => !rG.has(g));
    if (addedG.length) notes.push({ icon: '🎯', text: `G-codes added: ${addedG.join(', ')}` });
    if (removedG.length) notes.push({ icon: '🎯', text: `G-codes removed: ${removedG.join(', ')}` });

    if (!notes.length) notes.push({ icon: '✏️', text: 'Formatting / whitespace change only' });

    // Build mini SVG diagram if motion endpoints differ in XY
    if (L?.isMotion && R?.isMotion && (L.coords.X !== undefined || L.coords.Y !== undefined || R.coords.X !== undefined || R.coords.Y !== undefined)) {
        const lx = L.coords.X ?? 0, ly = L.coords.Y ?? 0;
        const rx = R.coords.X ?? 0, ry = R.coords.Y ?? 0;
        if (lx !== rx || ly !== ry) {
            // Normalize to 160x100 box around both points
            const minX = Math.min(lx, rx, 0), maxX = Math.max(lx, rx, 0);
            const minY = Math.min(ly, ry, 0), maxY = Math.max(ly, ry, 0);
            const w = Math.max(1, maxX - minX), h = Math.max(1, maxY - minY);
            const scale = Math.min(140 / w, 80 / h);
            const px = (x) => 10 + (x - minX) * scale;
            const py = (y) => 90 - (y - minY) * scale; // flip y for screen coords
            svg = (
                <svg width="160" height="100" style={{ background: 'var(--y-bg-soft, #fafafa)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 4 }}>
                    <line x1={px(0)} y1={py(0)} x2={px(lx)} y2={py(ly)} stroke="#9ca3af" strokeWidth="1" strokeDasharray="3 2" />
                    <line x1={px(0)} y1={py(0)} x2={px(rx)} y2={py(ry)} stroke="#15803d" strokeWidth="2" />
                    <circle cx={px(0)} cy={py(0)} r="3" fill="#1f2937" />
                    <circle cx={px(lx)} cy={py(ly)} r="3" fill="#b91c1c" />
                    <circle cx={px(rx)} cy={py(ry)} r="3" fill="#15803d" />
                    <text x={px(lx) + 5} y={py(ly) - 4} fontSize="9" fill="#b91c1c">orig</text>
                    <text x={px(rx) + 5} y={py(ry) - 4} fontSize="9" fill="#15803d">opt</text>
                </svg>
            );
        }
    }
    return { headline, notes, svg };
}

function DiffView({ original, optimized }) {
    const [mode, setMode] = useState('side'); // 'side' | 'unified' | 'raw'
    const [hoveredKey, setHoveredKey] = useState(null);
    const diff = useMemo(() => diffLines(original, optimized), [original, optimized]);
    const sideRows = useMemo(() => buildSideBySide(diff.segments), [diff]);

    // Build a map of rowIdx → explanation (lazy-compute on first hover via memoization)
    const explanationFor = (rowIdx) => {
        const row = sideRows[rowIdx];
        if (!row || row.kind === 'same') return null;
        const leftText = row.left?.text || '';
        const rightText = row.right?.text || '';
        return explainChange(leftText, rightText, row.kind);
    };
    const explanationForUnified = (segIdx) => {
        const s = diff.segments[segIdx];
        if (!s || s.type === '=') return null;
        const kind = s.type === '+' ? 'add' : 'del';
        return explainChange(s.type === '-' ? s.text : '', s.type === '+' ? s.text : '', kind);
    };

    const hoverExpl = hoveredKey != null
        ? (mode === 'side' ? explanationFor(hoveredKey) : explanationForUnified(hoveredKey))
        : null;

    const Tab = ({ id, label }) => (
        <button
            onClick={() => setMode(id)}
            style={{
                padding: '6px 14px', fontSize: 12, fontWeight: 700,
                background: mode === id ? '#7c3aed' : '#fff',
                color: mode === id ? '#fff' : '#7c3aed',
                border: '1px solid #7c3aed', borderRadius: 4, cursor: 'pointer',
            }}
        >{label}</button>
    );

    return (
        <div style={{ border: `1px solid ${DIFF_COLORS.border}`, borderRadius: 6, overflow: 'hidden' }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                background: '#faf5ff', borderBottom: `1px solid ${DIFF_COLORS.border}`, flexWrap: 'wrap',
            }}>
                <Tab id="side" label="Side-by-side" />
                <Tab id="unified" label="Unified" />
                <Tab id="raw" label="Raw optimized" />
                <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
                    <span style={{ color: DIFF_COLORS.addText, fontWeight: 800, fontSize: 14 }}>+{diff.adds}</span>{' '}
                    <span style={{ color: DIFF_COLORS.delText, fontWeight: 800, fontSize: 14 }}>−{diff.dels}</span>
                    {' '}lines{diff.truncated ? ' (truncated at 5000)' : ''}
                </span>
            </div>

            {mode === 'raw' && (
                <textarea
                    value={optimized || ''}
                    readOnly
                    style={{
                        width: '100%', height: 380, fontFamily: 'monospace', fontSize: 13,
                        padding: 12, border: 'none', resize: 'vertical',
                        background: '#faf5ff', color: '#1f2937', boxSizing: 'border-box', lineHeight: '19px',
                    }}
                />
            )}

            {mode === 'unified' && (
                <div style={{ maxHeight: 480, overflow: 'auto', background: 'var(--y-bg, #fff)' }}>
                    <pre style={{ margin: 0, padding: 0, fontFamily: 'monospace', fontSize: 13, lineHeight: '19px' }}>
                        {diff.segments.map((s, idx) => {
                            const bg = s.type === '+' ? DIFF_COLORS.addBg : s.type === '-' ? DIFF_COLORS.delBg : DIFF_COLORS.sameBg;
                            const color = s.type === '+' ? DIFF_COLORS.addText : s.type === '-' ? DIFF_COLORS.delText : '#374151';
                            const sign = s.type === '+' ? '+' : s.type === '-' ? '−' : ' ';
                            const ln = s.type === '+' ? `      ${(s.bIdx ?? 0) + 1}` : s.type === '-' ? `${(s.aIdx ?? 0) + 1}      ` : `${(s.aIdx ?? 0) + 1}  ${(s.bIdx ?? 0) + 1}`;
                            const isChange = s.type !== '=';
                            const isHovered = hoveredKey === idx;
                            return (
                                <div
                                    key={idx}
                                    onMouseEnter={isChange ? () => setHoveredKey(idx) : undefined}
                                    style={{
                                        display: 'flex', background: bg, color, padding: '0 8px',
                                        cursor: isChange ? 'help' : 'default',
                                        boxShadow: isHovered ? 'inset 3px 0 0 #7c3aed' : 'none',
                                    }}
                                >
                                    <span style={{ color: DIFF_COLORS.gutter, minWidth: 96, textAlign: 'right', paddingRight: 12, userSelect: 'none', fontSize: 12 }}>{ln}</span>
                                    <span style={{ width: 18, fontWeight: 700, userSelect: 'none' }}>{sign}</span>
                                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', flex: 1 }}>{s.text || ' '}</span>
                                </div>
                            );
                        })}
                    </pre>
                </div>
            )}

            {mode === 'side' && (
                <div style={{ maxHeight: 480, overflow: 'auto', background: 'var(--y-bg, #fff)', fontFamily: 'monospace', fontSize: 13, lineHeight: '19px' }}>
                    {/* Sticky headers — single row, shares the same grid layout as data rows below */}
                    <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr',
                        position: 'sticky', top: 0, zIndex: 1,
                        borderBottom: `1px solid ${DIFF_COLORS.border}`,
                    }}>
                        <div style={{ padding: '6px 12px', fontSize: 12, fontWeight: 800, color: '#6b7280', background: 'var(--y-bg-soft, #f9fafb)', letterSpacing: 0.6, borderRight: `1px solid ${DIFF_COLORS.border}` }}>ORIGINAL</div>
                        <div style={{ padding: '6px 12px', fontSize: 12, fontWeight: 800, color: '#7c3aed', background: '#faf5ff', letterSpacing: 0.6 }}>Yantra Intelligence-OPTIMIZED</div>
                    </div>
                    {/* Each row of the diff is one row in this grid — both left + right
                        cells live in the SAME grid row so they auto-share height when
                        one side has wrapped text and the other is empty. Fixes the
                        vertical drift bug. */}
                    {sideRows.map((row, idx) => {
                        const L = row.left, R = row.right;
                        const leftBg  = row.kind === 'add' ? '#f9fafb' : row.kind === 'del' ? DIFF_COLORS.delBg : row.kind === 'mod' ? DIFF_COLORS.modBg : DIFF_COLORS.sameBg;
                        const rightBg = row.kind === 'del' ? '#f9fafb' : row.kind === 'add' ? DIFF_COLORS.addBg : row.kind === 'mod' ? DIFF_COLORS.modBg : DIFF_COLORS.sameBg;
                        const leftColor  = row.kind === 'del' ? DIFF_COLORS.delText : '#374151';
                        const rightColor = row.kind === 'add' ? DIFF_COLORS.addText : '#374151';
                        const isChange = row.kind !== 'same';
                        const isHovered = hoveredKey === idx;
                        const cellStyle = {
                            padding: '0 8px', display: 'flex', minHeight: 19,
                            cursor: isChange ? 'help' : 'default',
                            boxShadow: isHovered ? 'inset 3px 0 0 #7c3aed' : 'none',
                            alignItems: 'stretch',
                        };
                        return (
                            <div key={idx}
                                onMouseEnter={isChange ? () => setHoveredKey(idx) : undefined}
                                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}
                            >
                                <div style={{ ...cellStyle, background: leftBg, color: leftColor, borderRight: `1px solid ${DIFF_COLORS.border}` }}>
                                    <span style={{ color: DIFF_COLORS.gutter, minWidth: 42, textAlign: 'right', paddingRight: 8, userSelect: 'none', fontSize: 12, flexShrink: 0 }}>{L ? (L.aIdx ?? 0) + 1 : ''}</span>
                                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', flex: 1 }}>{L ? (L.text || ' ') : ''}</span>
                                </div>
                                <div style={{ ...cellStyle, background: rightBg, color: rightColor }}>
                                    <span style={{ color: DIFF_COLORS.gutter, minWidth: 42, textAlign: 'right', paddingRight: 8, userSelect: 'none', fontSize: 12, flexShrink: 0 }}>{R ? (R.bIdx ?? 0) + 1 : ''}</span>
                                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', flex: 1 }}>{R ? (R.text || ' ') : ''}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Hover-explanation panel — appears under the diff when hovering a changed line */}
            {mode !== 'raw' && (
                <div
                    onMouseLeave={() => setHoveredKey(null)}
                    style={{
                        borderTop: `1px solid ${DIFF_COLORS.border}`,
                        background: hoverExpl ? '#fefce8' : '#f9fafb',
                        padding: '10px 14px',
                        minHeight: 80,
                        display: 'flex', gap: 14, alignItems: 'flex-start',
                    }}
                >
                    {hoverExpl ? (
                        <>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 14, fontWeight: 800, color: '#7c3aed', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#7c3aed', display: 'inline-block' }} />
                                    {hoverExpl.headline}
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', fontSize: 13, color: '#374151', lineHeight: 1.8 }}>
                                    {hoverExpl.notes.map((n, i) => (
                                        <li key={i} style={{ display: 'flex', gap: 6 }}>
                                            <span style={{ width: 16 }}>{n.icon}</span>
                                            <span>{n.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            {hoverExpl.svg && (
                                <div style={{ flexShrink: 0, textAlign: 'center' }}>
                                    <div style={{ fontSize: 9, color: '#6b7280', fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>TOOLPATH</div>
                                    {hoverExpl.svg}
                                    <div style={{ fontSize: 9, color: '#6b7280', marginTop: 4 }}>
                                        <span style={{ color: '#b91c1c' }}>● orig</span>{' '}
                                        <span style={{ color: '#15803d' }}>● opt</span>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>
                            💡 Hover any red / green / yellow line above to see why the Yantra Intelligence changed it.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const GcodeOptimizer = () => {
    const [code, setCode] = useState('');
    const [result, setResult] = useState(null);
    const [filename, setFilename] = useState('');
    const [aiSuggestions, setAiSuggestions] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiErr, setAiErr] = useState('');
    const [optimized, setOptimized] = useState(null);
    const [angle, setAngle] = useState(30);
    const [expandedSugg, setExpandedSugg] = useState(null);
    const [aiCode, setAiCode] = useState(null);          // { rewritten, stats: parsed, sizeDelta, lineDelta }
    const [aiCodeLoading, setAiCodeLoading] = useState(false);
    const [aiCodeErr, setAiCodeErr] = useState('');
    const [aiCodeFromCache, setAiCodeFromCache] = useState(false);

    // === Persistent cache for Yantra Intelligence-rewritten code ===
    // Keyed by a content hash of the program text so identical programs (any
    // machine) reuse the same result. Stored in localStorage — survives reloads,
    // tab switches, browser restarts. Cleared explicitly via "Re-run Yantra Intelligence" button.
    const strHash = (s) => {
        let h = 0;
        for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
        return h.toString(36);
    };
    const cacheKeyFor = (text) => `yantra-aicode-${strHash(text || '')}-${(text || '').length}`;
    const loadCachedAiCode = (text) => {
        try {
            const raw = localStorage.getItem(cacheKeyFor(text));
            if (!raw) return null;
            const j = JSON.parse(raw);
            return j;
        } catch { return null; }
    };
    const saveAiCodeToCache = (origText, aiCodeObj, suggestions) => {
        try {
            const payload = {
                text: aiCodeObj.text,
                lineDelta: aiCodeObj.lineDelta,
                timeDeltaSec: aiCodeObj.timeDeltaSec,
                rapidDeltaMm: aiCodeObj.rapidDeltaMm,
                toolChangeDelta: aiCodeObj.toolChangeDelta,
                sizeDelta: aiCodeObj.sizeDelta,
                suggestions: suggestions || [],
                savedAt: Date.now(),
            };
            localStorage.setItem(cacheKeyFor(origText), JSON.stringify(payload));
        } catch (e) {
            console.warn('Yantra Intelligence cache save failed:', e);
        }
    };
    const clearAiCodeCache = (text) => {
        try { localStorage.removeItem(cacheKeyFor(text)); } catch {}
    };
    // Right-column tab: 'analysis' (stats+3D) | 'suggestions' (Yantra Intelligence list) | 'output' (NcPlayer+Diff)
    const [activeTab, setActiveTab] = useState('analysis');
    // Component reference (image, PDF, cycle time, etc.) for the loaded machine
    const [componentInfo, setComponentInfo] = useState(null);
    // Popup state for full-screen image/PDF preview ({ url, isPdf, title }) or null
    const [popup, setPopup] = useState(null);
    useEffect(() => {
        if (!popup) return;
        const onKey = (e) => { if (e.key === 'Escape') setPopup(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [popup]);

    // Extract program number (O####) from loaded G-code — shown alongside machine name
    const programNumber = useMemo(() => {
        if (!code) return null;
        const m = code.match(/^\s*[%\s]*O(\d{1,8})\b/m);
        return m ? `O${m[1]}` : null;
    }, [code]);

    // Telemetry program loader
    const [programs, setPrograms] = useState([]);
    const [programsLoading, setProgramsLoading] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState('');
    const [loadingProgram, setLoadingProgram] = useState(false);

    // === Unique-programs view (program library across the fleet) ===
    // viewMode: 'machine' = original "pick a machine, see its program"
    //           'program' = NEW "pick a unique program, see machines that ran it"
    const [viewMode, setViewMode] = useState('machine');
    const [uniquePrograms, setUniquePrograms] = useState([]);
    const [uniqueLoading, setUniqueLoading] = useState(false);
    const [programHistory, setProgramHistory] = useState(null);

    // Program-library filter UI state
    const [progSearch,    setProgSearch]    = useState('');
    const [progMachineFilter, setProgMachineFilter] = useState([]); // array of machine names
    const [progMinLoads,  setProgMinLoads]  = useState(0);
    const [progSort,      setProgSort]      = useState('recent'); // recent | loads | program | machines

    const SERVER = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
    const customerId = (localStorage.getItem('CustomerID') || '').replace(/\\/g, '').replace(/"/g, '').trim();

    // === Fetch list of machines with G-code in telemetry ===
    useEffect(() => {
        if (!customerId) return;
        setProgramsLoading(true);
        axios.get(`${SERVER}/api/buddy/gcode/programs`, { params: { customer_id: customerId } })
            .then(r => setPrograms(Array.isArray(r.data) ? r.data : []))
            .catch(() => setPrograms([]))
            .finally(() => setProgramsLoading(false));
    }, [customerId, SERVER]);

    // === Fetch unique programs lazily — only when user toggles to 'program' view ===
    useEffect(() => {
        if (!customerId) return;
        if (viewMode !== 'program') return;
        if (uniquePrograms.length > 0) return;
        setUniqueLoading(true);
        axios.get(`${SERVER}/api/buddy/gcode/programs-unique`, { params: { customer_id: customerId, days: 7 } })
            .then(r => setUniquePrograms(Array.isArray(r.data) ? r.data : []))
            .catch(() => setUniquePrograms([]))
            .finally(() => setUniqueLoading(false));
    }, [viewMode, customerId, SERVER]);

    // === Drilldown — load run history for a clicked unique program ===
    const showProgramHistory = async (programNo) => {
        try {
            const r = await axios.get(`${SERVER}/api/buddy/gcode/program-history`, {
                params: { customer_id: customerId, program_no: programNo, days: 7 },
            });
            setProgramHistory({ programNo, events: Array.isArray(r.data) ? r.data : [] });
        } catch (e) {
            setProgramHistory({ programNo, events: [] });
        }
    };

    // Deep-link auto-load: when arriving with ?device=<uuid> (e.g. from the
    // Component Registration Yantra Intelligence modal "Open Animation" button), auto-pick
    // that machine AND switch to the Output tab (NcPlayer + Diff) where the
    // animation/before-after lives. Default activeTab is 'analysis' (stats);
    // deep-link overrides it because the supervisor clicked specifically to
    // see the animated Yantra Intelligence comparison, not the raw stats.
    useEffect(() => {
        if (!customerId) return;
        const params = new URLSearchParams(window.location.search);
        const deviceParam = params.get('device');
        if (!deviceParam) return;
        if (programs.length === 0 && programsLoading) return;
        // Switch to output tab BEFORE loadFromMachine so any internal tab
        // changes inside that function won't override our intent.
        setActiveTab('output');
        loadFromMachine(deviceParam);
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('device');
            url.searchParams.delete('component');
            window.history.replaceState({}, '', url.toString());
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, programs.length, programsLoading]);

    const loadFromMachine = async (deviceId) => {
        if (!deviceId) return;
        setSelectedDevice(deviceId);
        setLoadingProgram(true);
        setComponentInfo(null);
        try {
            const r = await axios.get(`${SERVER}/api/buddy/gcode/program`, { params: { device_id: deviceId } });
            const text = r.data?.text || '';
            if (!text) { alert('No program text available for this machine.'); return; }
            setCode(text);
            const prog = programs.find(p => p.device_id === deviceId);
            setFilename(prog ? `${prog.device_name}.nc` : 'machine_program.nc');
            // auto-analyze
            setResult(parseGcode(text));
            setAiSuggestions(null); setAiErr(''); setOptimized(null);
            setAiCode(null); setAiCodeErr(''); setAiCodeFromCache(false);
            // Check cache for previously-generated Yantra Intelligence output for this exact program text
            const cached = loadCachedAiCode(text);
            if (cached) {
                try {
                    const newStats = parseGcode(cached.text);
                    setAiCode({
                        text: cached.text,
                        stats: newStats,
                        sizeDelta: cached.sizeDelta,
                        lineDelta: cached.lineDelta,
                        timeDeltaSec: cached.timeDeltaSec,
                        rapidDeltaMm: cached.rapidDeltaMm,
                        toolChangeDelta: cached.toolChangeDelta,
                        cachedAt: cached.savedAt,
                    });
                    if (Array.isArray(cached.suggestions) && cached.suggestions.length > 0) {
                        setAiSuggestions(cached.suggestions);
                    }
                    setAiCodeFromCache(true);
                } catch (e) { console.warn('Cache restore failed:', e); }
            }
            // Fetch the component currently loaded on this machine + its registered
            // master data (image, PDF, cycle time, etc.) so we can display them
            // and pass to Yantra Intelligence for analysis.
            axios.get(`${SERVER}/api/buddy/gcode/component-for-machine`, {
                params: { device_id: deviceId, customer_id: customerId },
            }).then(cr => {
                setComponentInfo(cr.data || null);
                // Check backend cache (per-component master entry) for previously-
                // saved Yantra Intelligence output. Prefer it over localStorage so multi-user
                // teams share the same cached optimization.
                const compNum = cr.data?.master?.component_number;
                if (compNum) {
                    axios.get(`${SERVER}/api/buddy/gcode/get-ai-output`, {
                        params: { customer_id: customerId, component_number: compNum },
                    }).then(ar => {
                        const entry = ar.data;
                        if (!entry || entry.found === false || !entry.ai_text) return;
                        try {
                            const newStats = parseGcode(entry.ai_text);
                            const d = entry.deltas || {};
                            setAiCode({
                                text: entry.ai_text,
                                stats: newStats,
                                sizeDelta: d.sizeDelta || (entry.ai_text.length - text.length),
                                lineDelta: d.lineDelta || (newStats.lines - parseGcode(text).lines),
                                timeDeltaSec: d.timeDeltaSec || 0,
                                rapidDeltaMm: d.rapidDeltaMm || 0,
                                toolChangeDelta: d.toolChangeDelta || 0,
                                cachedAt: entry.saved_at,
                                serverCached: true,
                            });
                            if (Array.isArray(entry.suggestions) && entry.suggestions.length > 0) {
                                setAiSuggestions(entry.suggestions);
                            }
                            setAiCodeFromCache(true);
                        } catch (e) { console.warn('Backend Yantra Intelligence cache restore failed:', e); }
                    }).catch(() => { /* no server cache — fine */ });
                }
            }).catch(() => setComponentInfo(null));
        } catch (e) {
            alert('Failed to load program: ' + (e?.response?.data?.message || e?.message));
        } finally { setLoadingProgram(false); }
    };

    const fmtAgo = (ts) => {
        if (!ts) return '';
        const s = Math.round((Date.now() - ts) / 1000);
        if (s < 60) return `${s}s ago`;
        const m = Math.round(s / 60);
        if (m < 60) return `${m}m ago`;
        const h = Math.round(m / 60);
        if (h < 24) return `${h}h ago`;
        return `${Math.round(h / 24)}d ago`;
    };

    const fmtSize = (n) => n >= 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} B`;

    // Parse "~5-7 min", "30-60 sec", "1-3 min", "~3 min" → {loSec, hiSec, hasTime}
    // Picks the FIRST time mention so "5-7 min (10-12 sec each)" → 5-7 min.
    const parseSavings = (str) => {
        if (!str) return { loSec: 0, hiSec: 0, hasTime: false };
        const s = str.toLowerCase();
        // Find earliest min or sec pattern
        const reAll = /(\d+(?:\.\d+)?)\s*(?:-\s*(\d+(?:\.\d+)?))?\s*(min|sec|s\b)/g;
        let m;
        while ((m = reAll.exec(s)) !== null) {
            const lo = parseFloat(m[1]);
            const hi = m[2] ? parseFloat(m[2]) : lo;
            const unit = m[3];
            const mult = unit === 'min' ? 60 : 1;
            return { loSec: lo * mult, hiSec: hi * mult, hasTime: true };
        }
        return { loSec: 0, hiSec: 0, hasTime: false };
    };

    const handleFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 2 * 1024 * 1024) { alert('File too large (max 2 MB)'); return; }
        setFilename(f.name);
        const text = await f.text();
        setCode(text);
    };

    // === G-code parser ===
    const parseGcode = (text) => {
        const lines = text.split(/\r?\n/);
        const path = [];
        let x = 0, y = 0, z = 0, feed = 0, tool = 0, spindle = 0;
        let mode = 'G0';
        let totalRapid = 0, totalCut = 0, estTime = 0;
        const toolChanges = [];
        const tools = new Set();
        // Rapid feed (G0) assumption: 30 m/min — typical for modern Fanuc-class
        // VMCs like the ATECH machines. The previous value (10 m/min) was too
        // conservative and inflated cycle-time estimates by ~15-20 min on
        // rapid-heavy programs.
        const RAPID_FEED = 30000;
        // Tool change time in seconds added to estTime for each unique T change.
        // Modern ATC swap ~4-6 sec; we use 5 sec as a reasonable average.
        const TOOL_CHANGE_SEC = 5;

        // Convert program F to effective mm/min for time calculation.
        // Lathes typically default to G99 feed-per-revolution (mm/rev) instead
        // of G98 feed-per-minute (mm/min). When F is suspiciously small
        // (< 50 mm/min) AND a spindle RPM is set, treat as per-rev mode and
        // multiply F × RPM to get mm/min. Without this, lathe cycle estimates
        // explode (e.g. F0.1 on 1200 RPM should be 120 mm/min, not 0.1).
        const effectiveFeed = () => {
            if (feed > 0 && feed < 50 && spindle > 0) return feed * spindle;
            return feed;
        };

        // Macro variables (#NNN) and active work offset (G54-G59 -> {dx,dy,dz}).
        // Fanuc-style: #761=-586.56 (assignment) then later X#761 substitutes the value.
        // G10L2 P1..6 writes a work offset for that G-code; G54-G59 activates one.
        // We track these so X#771 / X100 in a G54 frame draws at the right world coords.
        const vars = {};
        const workOffsets = { 1: { x: 0, y: 0, z: 0 }, 2: { x: 0, y: 0, z: 0 }, 3: { x: 0, y: 0, z: 0 }, 4: { x: 0, y: 0, z: 0 }, 5: { x: 0, y: 0, z: 0 }, 6: { x: 0, y: 0, z: 0 } };
        let activeOffset = 0; // 0 = no offset, 1..6 = G54..G59

        // Canned cycle modal state (Fanuc G73/G81/G82/G83/G84/G85/G86/G89).
        // After a cycle is set, subsequent X/Y-only lines trigger the cycle at
        // those positions until G80 cancels. This is where NCViewer's drilling /
        // tapping geometry comes from — without expansion we miss most of the part.
        let cannedMode = null;   // 'G81' | 'G82' | 'G83' | 'G73' | 'G84' | 'G85' | 'G86' | 'G89' | null
        let cannedR = 0;          // R retract plane (modal)
        let cannedZ = 0;          // Z bottom depth (modal)
        let cannedQ = 0;          // Q peck depth (modal)
        let returnMode = 'G98';   // G98 = return to initial Z; G99 = return to R
        let initialZ = null;      // Z before cycle started (for G98 return)

        const evalNum = (s) => {
            if (s == null) return null;
            let str = String(s).trim();
            // Substitute #NNN tokens with their values (recursive 1-level)
            str = str.replace(/#(\d+)/g, (_, n) => (vars[n] != null ? String(vars[n]) : '0'));
            // Strip surrounding brackets [ ]
            str = str.replace(/^\[/, '').replace(/\]$/, '');
            // CRITICAL: try expression eval FIRST when the string contains an
            // operator past position 0. parseFloat is too greedy — for
            // "4.3-2.0" it returns 4.3 (stops at the minus), which silently
            // broke `#100 = #100 - 2.0` and made WHILE loops never decrement.
            if (/^[\d.\s+\-*/().]+$/.test(str) && /[+\-*/]/.test(str.slice(1))) {
                try {
                    // eslint-disable-next-line no-new-func
                    const v = Function('return (' + str + ')')();
                    if (Number.isFinite(v)) return v;
                } catch {/* fall through */}
            }
            const n = parseFloat(str);
            return Number.isFinite(n) ? n : null;
        };

        // Evaluate Fanuc-style WHILE condition: [expr GE/LE/EQ/NE/GT/LT expr]
        // Used to decide whether to iterate the loop body again.
        const evalCondition = (condStr) => {
            const m = String(condStr).match(/^(.+?)\s*(GE|LE|EQ|NE|GT|LT)\s*(.+)$/i);
            if (!m) return false;
            const lhs = evalNum(m[1]);
            const rhs = evalNum(m[3]);
            if (lhs == null || rhs == null) return false;
            switch (m[2].toUpperCase()) {
                case 'GE': return lhs >= rhs;
                case 'LE': return lhs <= rhs;
                case 'EQ': return Math.abs(lhs - rhs) < 1e-6;
                case 'NE': return Math.abs(lhs - rhs) >= 1e-6;
                case 'GT': return lhs > rhs;
                case 'LT': return lhs < rhs;
                default: return false;
            }
        };

        // Forward-scan for matching ENDn at the same nesting level.
        // (Don't pre-index — Fanuc programs reuse DO1/END1 dozens of times,
        // so a label→line map would always point to the wrong pair.)
        const findMatchingEnd = (fromIdx, label) => {
            let depth = 1;
            const whileRe = new RegExp(`^WHILE\\s*\\[.+?\\]\\s*DO\\s*${label}\\b`, 'i');
            const endRe = new RegExp(`^END\\s*${label}\\b`, 'i');
            for (let j = fromIdx + 1; j < lines.length; j++) {
                const ln = lines[j].replace(/\([^)]*\)/g, '').replace(/;.*/, '').trim();
                if (whileRe.test(ln)) depth++;
                if (endRe.test(ln)) {
                    depth--;
                    if (depth === 0) return j;
                }
            }
            return -1;
        };

        // Loop stack: each frame { label, startLine, condStr, iterations }
        // iterations cap protects against infinite loops if condition eval is buggy
        const loopStack = [];
        const MAX_LOOP_ITERS = 500;
        const MAX_PATH_SEGMENTS = 200000; // hard cap to keep the SVG renderer sane

        // Index-based iteration so WHILE/DO/END can jump back & forth
        let totalLineHits = 0;
        const MAX_LINE_HITS = lines.length * 200; // outer safety: ~200x program length
        let i = 0;
        while (i < lines.length && totalLineHits++ < MAX_LINE_HITS) {
            const lineNo = i + 1;
            const rawLine = lines[i];
            let line = rawLine.replace(/\([^)]*\)/g, '').replace(/;.*/, '').trim();
            if (!line) { i++; continue; }
            line = line.replace(/^\//, '').replace(/^N\d+\s*/i, '');

            // Safety: stop expanding segments past a hard cap so we don't OOM the SVG renderer
            if (path.length > MAX_PATH_SEGMENTS) break;

            // WHILE[cond]DOn — start a loop iteration. If condition false, skip past matching ENDn.
            const whileMatch = line.match(/^WHILE\s*\[(.+?)\]\s*DO\s*(\d+)/i);
            if (whileMatch) {
                const condStr = whileMatch[1];
                const label = whileMatch[2];
                if (evalCondition(condStr)) {
                    const existing = loopStack.find(f => f.startLine === i);
                    if (!existing) {
                        loopStack.push({ label, startLine: i, condStr, iterations: 0 });
                    }
                    i++;
                } else {
                    // Forward-scan for the matching ENDn at the same nesting depth
                    const endLine = findMatchingEnd(i, label);
                    i = endLine >= 0 ? endLine + 1 : i + 1;
                }
                continue;
            }

            // ENDn — return to matching WHILE if condition still true
            const endMatch = line.match(/^END\s*(\d+)/i);
            if (endMatch) {
                const label = endMatch[1];
                // Find the topmost frame matching this label (handles non-matching outer pop)
                let topIdx = -1;
                for (let k = loopStack.length - 1; k >= 0; k--) {
                    if (loopStack[k].label === label) { topIdx = k; break; }
                }
                if (topIdx >= 0) {
                    const top = loopStack[topIdx];
                    top.iterations++;
                    if (top.iterations < MAX_LOOP_ITERS && evalCondition(top.condStr)) {
                        i = top.startLine + 1;
                    } else {
                        loopStack.splice(topIdx, 1);
                        i++;
                    }
                } else {
                    i++;
                }
                continue;
            }

            // Variable assignment: #NNN = value (e.g. "#761=-586.56" or "#761 = #762 + 10")
            const assign = line.match(/^#(\d+)\s*=\s*(.+)$/);
            if (assign) {
                const v = evalNum(assign[2]);
                if (v != null) vars[assign[1]] = v;
                i++; continue;
            }

            // Tokenize: capture single letter + (signed number OR #NNN reference).
            // Collect ALL G-codes per line into a list — Fanuc lines often pack
            // multiple G-codes onto one row (e.g. "G02G90I26.4Z-5" has G2 AND G90).
            // Previously the last G value overwrote earlier ones, so arc commands
            // were lost when paired with absolute-mode flags. CRITICAL fix.
            const tokens = {};
            const gList = [];
            line.replace(/([A-Z])(-?#?\d+\.?\d*)/gi, (_, l, v) => {
                const letter = l.toUpperCase();
                const val = evalNum(v);
                if (letter === 'G') gList.push(val);
                else tokens[letter] = val;
                return '';
            });
            if (gList.length) tokens.G = gList[gList.length - 1]; // legacy backref for code below

            // G10 L2 Pn X Y Z — work offset WRITE, not a motion
            if (gList.includes(10) && tokens.L === 2 && tokens.P != null) {
                const p = Math.floor(tokens.P);
                if (workOffsets[p]) {
                    if (tokens.X != null) workOffsets[p].x = tokens.X;
                    if (tokens.Y != null) workOffsets[p].y = tokens.Y;
                    if (tokens.Z != null) workOffsets[p].z = tokens.Z;
                }
                i++; continue;
            }

            // G65 P#### — macro subprogram call. Subprogram source lives on the
            // machine controller, not in the .nc file, so we can't expand it.
            // Skip the line entirely so its B/U/V/W parameters don't get
            // misinterpreted as motion or tool changes.
            if (gList.includes(65) || gList.includes(66)) { i++; continue; }

            // Process EVERY G-code on this line in order so e.g. G02+G90+G54
            // all apply (arc + absolute mode + work offset).
            for (const gn of gList) {
                const g = Math.floor(gn);
                if (g === 0) mode = 'G0';
                else if (g === 1) mode = 'G1';
                else if (g === 2) mode = 'G2';
                else if (g === 3) mode = 'G3';
                else if (g >= 54 && g <= 59) activeOffset = g - 53; // G54→1
                else if (g === 80) { cannedMode = null; initialZ = null; }
                else if ([73, 81, 82, 83, 84, 85, 86, 89].includes(g)) {
                    cannedMode = 'G' + g;
                    if (initialZ == null) initialZ = z;
                    if (tokens.R != null) cannedR = tokens.R + (activeOffset > 0 ? workOffsets[activeOffset].z : 0);
                    if (tokens.Z != null) cannedZ = tokens.Z + (activeOffset > 0 ? workOffsets[activeOffset].z : 0);
                    if (tokens.Q != null) cannedQ = tokens.Q;
                }
                else if (g === 98) returnMode = 'G98';
                else if (g === 99) returnMode = 'G99';
            }
            if (tokens.F !== undefined && tokens.F !== null) feed = tokens.F;
            if (tokens.T !== undefined && tokens.T !== null && tokens.T !== tool) {
                toolChanges.push({ line: lineNo, from: tool, to: tokens.T });
                tool = tokens.T;
                tools.add(tool);
            }
            if (tokens.S !== undefined && tokens.S !== null) spindle = tokens.S;

            const hasMove = tokens.X != null || tokens.Y != null || tokens.Z != null;
            if (!hasMove) { i++; continue; }

            // Apply active work offset to absolute coords (best-effort — we don't
            // distinguish G90 absolute vs G91 incremental modally here).
            const ofs = activeOffset > 0 ? workOffsets[activeOffset] : { x: 0, y: 0, z: 0 };
            const nx = tokens.X != null ? tokens.X + ofs.x : x;
            const ny = tokens.Y != null ? tokens.Y + ofs.y : y;
            const nz = tokens.Z != null ? tokens.Z + ofs.z : z;

            // === Canned cycle expansion (Fanuc G73/G81-G86/G89) ===
            // When a canned cycle is active, X/Y motion triggers a full
            // drill/peck/tap cycle at the new position. Expand into multiple
            // segments so the 3D view shows the actual hole geometry that
            // NCViewer / CAM software renders.
            if (cannedMode && hasMove) {
                const tx = nx, ty = ny;
                const r = cannedR;
                const zb = cannedZ;
                const q = Math.abs(cannedQ) || 0;
                const retract = returnMode === 'G99' ? r : (initialZ != null ? initialZ : r);
                const pushSeg = (type, x1, y1, z1, x2, y2, z2) => {
                    const dxs = x2 - x1, dys = y2 - y1, dzs = z2 - z1;
                    const lens = Math.sqrt(dxs * dxs + dys * dys + dzs * dzs);
                    const s = { type, x1, y1, z1, x2, y2, z2, len: lens, feed, line: lineNo, tool };
                    path.push(s);
                    if (type === 'G0') { totalRapid += lens; estTime += (lens / RAPID_FEED) * 60; }
                    else { totalCut += lens; const ef = effectiveFeed(); if (ef > 0) estTime += (lens / ef) * 60; }
                };
                // 1. Rapid to (tx, ty) at current Z (typically the initial Z)
                pushSeg('G0', x, y, z, tx, ty, z);
                // 2. Rapid down to R plane
                if (z !== r) pushSeg('G0', tx, ty, z, tx, ty, r);
                // 3. The drill/peck/tap motion itself
                if (cannedMode === 'G83' || cannedMode === 'G73') {
                    // Peck drilling: feed down by Q, retract, repeat
                    let depth = r;
                    const dir = zb < r ? -1 : 1; // typically drilling downward (zb < r)
                    let safety = 0;
                    while ((dir < 0 ? depth > zb : depth < zb) && safety++ < 500) {
                        const nextDepth = dir < 0 ? Math.max(zb, depth - q) : Math.min(zb, depth + q);
                        pushSeg('G1', tx, ty, depth, tx, ty, nextDepth);
                        depth = nextDepth;
                        if ((dir < 0 ? depth > zb : depth < zb) && q > 0) {
                            pushSeg('G0', tx, ty, depth, tx, ty, r); // retract for chip clearance
                            const back = dir < 0 ? depth + 0.5 : depth - 0.5;
                            pushSeg('G0', tx, ty, r, tx, ty, back); // back to just above last cut
                            depth = back;
                        }
                    }
                } else if (cannedMode === 'G84' || cannedMode === 'G85' || cannedMode === 'G86' || cannedMode === 'G89') {
                    // Tapping / boring: feed down then feed (not rapid) back up
                    pushSeg('G1', tx, ty, r, tx, ty, zb);
                    pushSeg('G1', tx, ty, zb, tx, ty, r);
                } else {
                    // G81 / G82 (basic drilling): feed down, rapid retract
                    pushSeg('G1', tx, ty, r, tx, ty, zb);
                }
                // 4. Rapid retract to initialZ (G98) or R (G99)
                pushSeg('G0', tx, ty, cannedMode === 'G84' || cannedMode === 'G85' || cannedMode === 'G86' || cannedMode === 'G89' ? r : zb, tx, ty, retract);
                // Update current position
                x = tx; y = ty; z = retract;
                i++; continue;
            }

            // === G02/G03 arc tessellation (handles helical interpolation too) ===
            // For circular interpolation, replace the single straight chord with
            // many short line segments around the arc, with Z linearly interpolated
            // for helical moves. Without this, full-circle bore cuts collapse to
            // a single zero-length XY motion + Z drop = invisible cylinders.
            if ((mode === 'G2' || mode === 'G3') && (tokens.I != null || tokens.J != null || tokens.R != null)) {
                let cx, cy;
                if (tokens.I != null || tokens.J != null) {
                    cx = x + (tokens.I || 0);
                    cy = y + (tokens.J || 0);
                } else {
                    // R-form fallback — draw as straight chord
                    const dx2 = nx - x, dy2 = ny - y, dz2 = nz - z;
                    const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2 + dz2 * dz2);
                    path.push({ type: mode, x1: x, y1: y, z1: z, x2: nx, y2: ny, z2: nz, len: len2, feed, line: lineNo, tool });
                    totalCut += len2;
                    const ef2 = effectiveFeed();
                    if (ef2 > 0) estTime += (len2 / ef2) * 60;
                    x = nx; y = ny; z = nz; i++; continue;
                }
                const radius = Math.hypot(x - cx, y - cy);
                const a0 = Math.atan2(y - cy, x - cx);
                const a1 = Math.atan2(ny - cy, nx - cx);
                let sweep = a1 - a0;
                if (mode === 'G2') { while (sweep > 0) sweep -= 2 * Math.PI; if (sweep === 0) sweep = -2 * Math.PI; }
                else { while (sweep < 0) sweep += 2 * Math.PI; if (sweep === 0) sweep = 2 * Math.PI; }
                const segs = Math.max(8, Math.min(48, Math.ceil(Math.abs(sweep) * radius / 1.5))); // ~1.5mm chord
                let px = x, py = y, pz = z;
                for (let k = 1; k <= segs; k++) {
                    const t = k / segs;
                    const ang = a0 + sweep * t;
                    const xx = cx + radius * Math.cos(ang);
                    const yy = cy + radius * Math.sin(ang);
                    const zz = z + (nz - z) * t;
                    const sdx = xx - px, sdy = yy - py, sdz = zz - pz;
                    const slen = Math.sqrt(sdx * sdx + sdy * sdy + sdz * sdz);
                    path.push({ type: mode, x1: px, y1: py, z1: pz, x2: xx, y2: yy, z2: zz, len: slen, feed, line: lineNo, tool });
                    totalCut += slen;
                    const efA = effectiveFeed();
                    if (efA > 0) estTime += (slen / efA) * 60;
                    px = xx; py = yy; pz = zz;
                }
                x = nx; y = ny; z = nz; i++; continue;
            }

            const dx = nx - x, dy = ny - y, dz = nz - z;
            const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
            const seg = { type: mode, x1: x, y1: y, z1: z, x2: nx, y2: ny, z2: nz, len, feed, line: lineNo, tool };
            path.push(seg);

            if (mode === 'G0') {
                totalRapid += len;
                estTime += (len / RAPID_FEED) * 60;
            } else {
                totalCut += len;
                const efB = effectiveFeed();
                if (efB > 0) estTime += (len / efB) * 60;
            }
            x = nx; y = ny; z = nz;
            i++;
        }

        // Bounding box — compute with a manual loop, NOT Math.min(...spread),
        // which blows the call stack at ~100k+ segments (spread args limit).
        let xMin = Infinity, xMax = -Infinity;
        let yMin = Infinity, yMax = -Infinity;
        let zMin = Infinity, zMax = -Infinity;
        for (const p of path) {
            if (p.x1 < xMin) xMin = p.x1; if (p.x1 > xMax) xMax = p.x1;
            if (p.x2 < xMin) xMin = p.x2; if (p.x2 > xMax) xMax = p.x2;
            if (p.y1 < yMin) yMin = p.y1; if (p.y1 > yMax) yMax = p.y1;
            if (p.y2 < yMin) yMin = p.y2; if (p.y2 > yMax) yMax = p.y2;
            if (p.z1 < zMin) zMin = p.z1; if (p.z1 > zMax) zMax = p.z1;
            if (p.z2 < zMin) zMin = p.z2; if (p.z2 > zMax) zMax = p.z2;
        }
        const bbox = path.length > 0
            ? { xMin, xMax, yMin, yMax, zMin, zMax }
            : { xMin: 0, xMax: 100, yMin: 0, yMax: 100, zMin: 0, zMax: 10 };

        const rapidTime = path.filter((p) => p.type === 'G0').reduce((a, p) => a + (p.len / RAPID_FEED) * 60, 0);
        const rapidPct = estTime > 0 ? Math.round((rapidTime / estTime) * 100) : 0;

        // === Render-time decimation ===
        // For programs with > 20k segments, sample every Nth segment for the
        // rendered path. Visually identical (you can't see < 0.05 mm anyway)
        // but keeps the React/SVG render loop fast and the browser responsive.
        const RENDER_CAP = 20000;
        let renderPath = path;
        if (path.length > RENDER_CAP) {
            const step = Math.ceil(path.length / RENDER_CAP);
            renderPath = [];
            for (let k = 0; k < path.length; k += step) renderPath.push(path[k]);
        }

        // Add ATC tool-change overhead to estTime — real machines lose
        // ~4-6 sec per tool swap that pure motion calc misses.
        const toolChangeSecTotal = toolChanges.length * TOOL_CHANGE_SEC;
        const estTimeWithATC = estTime + toolChangeSecTotal;

        return {
            lines: lines.length, segments: path.length,
            renderSegments: renderPath.length,
            totalRapid: Math.round(totalRapid), totalCut: Math.round(totalCut),
            totalDistance: Math.round(totalRapid + totalCut),
            estTimeSec: Math.round(estTimeWithATC),
            estTimeMin: Math.round(estTimeWithATC / 60),
            toolChangeSec: toolChangeSecTotal,
            toolCount: tools.size, toolChanges: toolChanges.length, tools: [...tools],
            path: renderPath, bbox, rapidPct, rapidTime: Math.round(rapidTime),
        };
    };

    const analyze = () => {
        if (!code.trim()) { alert('Paste G-code or upload a file first'); return; }
        setResult(parseGcode(code));
        setAiSuggestions(null); setAiErr(''); setOptimized(null);
    };

    // Build component reference payload for Yantra Intelligence calls — server-side absolute
    // paths of image + PDF so Yantra Intelligence can Read them, plus the master info
    // (name, number, cycle time, etc.) as context.
    const componentRefForAI = () => {
        if (!componentInfo?.master) return {};
        const m = componentInfo.master;
        const cust = (customerId || '').replace(/[^a-zA-Z0-9-]/g, '');
        const toAbs = (urlPath) => {
            if (!urlPath) return null;
            // urlPath looks like "/component-images/<cust>/<file>.jpg"
            // mapped on disk to /home/yantra/TB/component_images/<cust>/<file>.jpg
            const m2 = urlPath.match(/^\/component-images\/(.+)$/);
            if (m2) return `/home/yantra/TB/component_images/${m2[1]}`;
            return urlPath;
        };
        return {
            componentImagePath: toAbs(m.image_url),
            componentDiagramPath: toAbs(m.diagram_url),
            componentInfo: {
                component_name: m.component_name,
                component_number: m.component_number,
                cycle_time: m.cycle_time,
                handling_time: m.handling_time,
                setup_time: m.setup_time,
                program_no: m.program_no,
                cost_per_part: m.cost_per_part,
            },
        };
    };

    const getAi = async () => {
        if (!result) return;
        setAiLoading(true); setAiErr('');
        try {
            const sample = code.split('\n').slice(0, 200).join('\n');
            const r = await axios.post(`${SERVER}/api/buddy/gcode/ai-suggest`, {
                stats: { ...result, path: undefined },
                sample, filename: filename || 'untitled.nc',
                customer_id: customerId,
                ...componentRefForAI(),
            });
            const suggestions = r.data.suggestions || [];
            setAiSuggestions(suggestions);
            setActiveTab('suggestions');
            // Persist suggestions to Component Master alongside any existing Yantra Intelligence
            // rewrite for this component, so they're restored across sessions.
            // We reuse save-ai-output with a minimal payload — the backend
            // merges with any existing ai_text for this component_number.
            const compNum = componentInfo?.master?.component_number;
            if (compNum) {
                axios.post(`${SERVER}/api/buddy/gcode/save-ai-output`, {
                    customer_id: customerId,
                    component_number: compNum,
                    program_no: componentInfo?.master?.program_no || null,
                    ai_text: aiCode?.text || '',   // keep any existing rewrite text
                    suggestions: suggestions,
                    deltas: aiCode ? {
                        lineDelta: aiCode.lineDelta, timeDeltaSec: aiCode.timeDeltaSec,
                        rapidDeltaMm: aiCode.rapidDeltaMm, toolChangeDelta: aiCode.toolChangeDelta,
                        sizeDelta: aiCode.sizeDelta,
                    } : {},
                }).catch((e) => console.warn('Save suggestions to component master failed:', e?.message));
            }
        } catch (e) {
            setAiErr(e?.response?.data?.message || e?.message || 'Yantra Intelligence suggest failed');
        } finally { setAiLoading(false); }
    };

    // === Yantra Intelligence rewrite: Claude generates an optimized G-code program ===
    // Realistic time estimate based on code size — the Yantra Intelligence rewrite time scales
    // with output tokens, which is roughly proportional to input size. Matches
    // the dynamic max_tokens setting in the backend. Used in both the
    // button label and the loading panel so the user sees one consistent ETA.
    const estimateAiTime = (chars) => {
        if (!chars) return '~30s';
        if (chars <  3000) return '~15-30s';
        if (chars < 10000) return '~30-60s';
        if (chars < 25000) return '~1-2 min';
        if (chars < 50000) return '~2-3 min';
        return '~3-5 min';
    };

    // Consolidated Yantra Intelligence pipeline: suggest → cut rapid travel → Yantra Intelligence rewrite, all in
    // one click. Replaces the 3 separate buttons. Pipeline halts on error and
    // surfaces it; if Yantra Intelligence returns SLOWER output, banner appears letting user discard.
    const runFullAiPipeline = async () => {
        if (!result || !code) { alert('Analyze a program first.'); return; }
        setAiCodeErr('');
        // Stage 1 — suggestions (skip if already have them)
        if (!aiSuggestions) {
            setAiLoading(true);
            try {
                const sample = code.split('\n').slice(0, 200).join('\n');
                const rs = await axios.post(`${SERVER}/api/buddy/gcode/ai-suggest`, {
                    stats: { ...result, path: undefined },
                    sample, filename: filename || 'untitled.nc',
                    customer_id: customerId, ...componentRefForAI(),
                });
                setAiSuggestions(rs.data.suggestions || []);
            } catch (e) {
                setAiCodeErr('Get suggestions failed: ' + (e?.response?.data?.message || e?.message || 'unknown'));
                setAiLoading(false);
                return;
            } finally { setAiLoading(false); }
        }
        // Stage 2 — instant rapid-travel cut (local algorithm, no network)
        optimize();
        // Stage 3 — full Yantra Intelligence rewrite
        await generateAiCode(false);
    };

    const generateAiCode = async (forceFresh = false) => {
        if (!result) { alert('Analyze a program first.'); return; }
        // If a cached result exists for this exact program text AND user
        // didn't force-fresh, just restore it instead of calling the Yantra Intelligence again.
        if (!forceFresh) {
            const cached = loadCachedAiCode(code);
            if (cached) {
                try {
                    const newStats = parseGcode(cached.text);
                    setAiCode({
                        text: cached.text, stats: newStats,
                        sizeDelta: cached.sizeDelta, lineDelta: cached.lineDelta,
                        timeDeltaSec: cached.timeDeltaSec, rapidDeltaMm: cached.rapidDeltaMm,
                        toolChangeDelta: cached.toolChangeDelta, cachedAt: cached.savedAt,
                    });
                    if (Array.isArray(cached.suggestions) && cached.suggestions.length > 0) {
                        setAiSuggestions(cached.suggestions);
                    }
                    setAiCodeFromCache(true);
                    setActiveTab('output');
                    return;
                } catch (e) { console.warn('Cache restore failed, will re-run:', e); }
            }
        } else {
            // User explicitly chose to re-run — wipe the cache for this program
            clearAiCodeCache(code);
        }
        setAiCodeLoading(true); setAiCodeErr(''); setAiCode(null); setAiCodeFromCache(false);
        // If no suggestions yet, fetch them inline so Yantra Intelligence knows what to apply
        let suggestionsToUse = aiSuggestions;
        if (!suggestionsToUse) {
            try {
                const sample = code.split('\n').slice(0, 200).join('\n');
                const rs = await axios.post(`${SERVER}/api/buddy/gcode/ai-suggest`, {
                    stats: { ...result, path: undefined }, sample, filename: filename || 'untitled.nc',
                    customer_id: customerId,
                    ...componentRefForAI(),
                });
                suggestionsToUse = rs.data.suggestions || [];
                setAiSuggestions(suggestionsToUse);
            } catch (e) {
                /* keep going with empty suggestions */
                suggestionsToUse = [];
            }
        }
        // Wrap the API call so a single timeout transparently retries once.
        // Large programs (34KB+ V07) occasionally hit the 6-min ceiling on the
        // first try; one retry usually succeeds because suggestions are cached.
        const callAiRewrite = async (attempt = 1) => {
            try {
                return await axios.post(`${SERVER}/api/buddy/gcode/ai-rewrite`, {
                    code, suggestions: suggestionsToUse, filename: filename || 'untitled.nc',
                    stats: { ...result, path: undefined },
                    customer_id: customerId,
                }, { timeout: 360000 });
            } catch (err) {
                const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
                if (isTimeout && attempt === 1) {
                    setAiCodeErr('Yantra Intelligence timed out — retrying once…');
                    return await callAiRewrite(2);
                }
                throw err;
            }
        };
        try {
            const r = await callAiRewrite();
            const rewritten = r.data?.rewritten || '';
            if (!rewritten) { setAiCodeErr('Yantra Intelligence returned empty code'); return; }
            // Parse the rewritten code to show before/after stats
            const newStats = parseGcode(rewritten);
            const aiCodeObj = {
                text: rewritten,
                stats: newStats,
                sizeDelta: rewritten.length - code.length,
                lineDelta: newStats.lines - result.lines,
                timeDeltaSec: result.estTimeSec - newStats.estTimeSec,
                rapidDeltaMm: result.totalRapid - newStats.totalRapid,
                toolChangeDelta: result.toolChanges - newStats.toolChanges,
            };
            setAiCode(aiCodeObj);
            // Persist to localStorage (browser-side) AND to backend (per-component
            // in the customer's master, survives across users/devices).
            saveAiCodeToCache(code, aiCodeObj, suggestionsToUse);
            const compNum = componentInfo?.master?.component_number;
            if (compNum) {
                axios.post(`${SERVER}/api/buddy/gcode/save-ai-output`, {
                    customer_id: customerId,
                    component_number: compNum,
                    program_no: componentInfo?.master?.program_no || null,
                    ai_text: rewritten,
                    suggestions: suggestionsToUse,
                    deltas: {
                        lineDelta: aiCodeObj.lineDelta,
                        timeDeltaSec: aiCodeObj.timeDeltaSec,
                        rapidDeltaMm: aiCodeObj.rapidDeltaMm,
                        toolChangeDelta: aiCodeObj.toolChangeDelta,
                        sizeDelta: aiCodeObj.sizeDelta,
                    },
                }).catch((e) => console.warn('Backend Yantra Intelligence cache save failed:', e?.message));
            }
            setAiCodeFromCache(false);
            setActiveTab('output');
        } catch (e) {
            setAiCodeErr(e?.response?.data?.message || e?.message || 'Yantra Intelligence rewrite failed');
        } finally { setAiCodeLoading(false); }
    };

    const downloadAiCode = () => {
        if (!aiCode?.text) return;
        const base = (filename || 'optimized.nc').replace(/\.[^.]+$/, '');
        const blob = new Blob([aiCode.text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${base}_AI_OPTIMIZED.nc`; a.click();
        URL.revokeObjectURL(url);
    };

    const copyAiCode = async () => {
        if (!aiCode?.text) return;
        try { await navigator.clipboard.writeText(aiCode.text); alert('Optimized G-code copied to clipboard'); }
        catch { alert('Copy failed — please select the text manually'); }
    };

    const optimize = async () => {
        if (!result) return;
        // Also fire Yantra Intelligence suggest if not already loaded (so user gets the WHY alongside the visual)
        if (!aiSuggestions && !aiLoading) { getAi(); }
        const p = result.path;
        const groups = [];
        let cur = null;
        for (const seg of p) {
            if (seg.type === 'G0') { if (cur) { groups.push(cur); cur = null; } continue; }
            if (!cur) cur = { entry: { x: seg.x1, y: seg.y1, z: seg.z1 }, exit: null, segs: [] };
            cur.segs.push(seg);
            cur.exit = { x: seg.x2, y: seg.y2, z: seg.z2 };
        }
        if (cur) groups.push(cur);
        if (groups.length < 2) { alert('Not enough cut groups to reorder'); return; }

        const remaining = [...groups];
        const ordered = [remaining.shift()];
        let pos = ordered[0].exit;
        while (remaining.length > 0) {
            let bestIdx = 0, bestDist = Infinity;
            for (let i = 0; i < remaining.length; i++) {
                const g = remaining[i];
                const d = Math.hypot(g.entry.x - pos.x, g.entry.y - pos.y) + Math.abs(g.entry.z - pos.z);
                if (d < bestDist) { bestDist = d; bestIdx = i; }
            }
            const next = remaining.splice(bestIdx, 1)[0];
            ordered.push(next);
            pos = next.exit;
        }

        const newPath = [];
        let lastPos = { x: 0, y: 0, z: 0 };
        for (const g of ordered) {
            const rd = Math.hypot(g.entry.x - lastPos.x, g.entry.y - lastPos.y) + Math.abs(g.entry.z - lastPos.z);
            if (rd > 0.01) {
                newPath.push({ type: 'G0', x1: lastPos.x, y1: lastPos.y, z1: lastPos.z, x2: g.entry.x, y2: g.entry.y, z2: g.entry.z, len: rd, feed: 0, line: 0 });
            }
            for (const s of g.segs) newPath.push(s);
            lastPos = g.exit;
        }

        const RAPID_FEED = 10000;
        const newTotalRapid = newPath.filter(s => s.type === 'G0').reduce((a, s) => a + s.len, 0);
        const newTotalCut = newPath.filter(s => s.type !== 'G0').reduce((a, s) => a + s.len, 0);
        const newRapidTime = (newTotalRapid / RAPID_FEED) * 60;
        const cutTime = newPath.filter(s => s.type !== 'G0' && s.feed > 0).reduce((a, s) => a + (s.len / s.feed) * 60, 0);
        const newEst = newRapidTime + cutTime;

        const savedSec = result.estTimeSec - newEst;
        const savedRapidMm = result.totalRapid - newTotalRapid;
        const savedPct = result.estTimeSec > 0 ? (savedSec / result.estTimeSec) * 100 : 0;

        setOptimized({
            path: newPath, bbox: result.bbox,
            totalRapid: Math.round(newTotalRapid), totalCut: Math.round(newTotalCut),
            estTimeSec: Math.round(newEst), savedSec: Math.round(savedSec),
            savedRapidMm: Math.round(savedRapidMm), savedPct: Math.round(savedPct * 10) / 10,
            groupsReordered: ordered.length,
        });
        // Auto-switch to the Yantra Intelligence Output tab so the user immediately sees the
        // "Algorithmic Optimization Result" panel + the green-vs-red overlay
        // on the 3D view (in Analysis tab).
        setActiveTab('output');
    };

    const fmtTime = (s) => {
        s = Math.round(s || 0);
        const m = Math.floor(s / 60), sec = s % 60;
        if (m >= 60) { const h = Math.floor(m / 60); return `${h}h ${m % 60}m`; }
        return `${m}m ${sec}s`;
    };

    const project = (xVal, yVal, zVal, bbox, w, h, rotYaw = 30, rotPitch = 60) => {
        const pad = 30;
        const yawRad = (rotYaw * Math.PI) / 180;
        const pitchRad = (rotPitch * Math.PI) / 180;
        const cy_ = Math.cos(yawRad), sy_ = Math.sin(yawRad);
        const cp_ = Math.cos(pitchRad), sp_ = Math.sin(pitchRad);
        const rot = (x, y, z) => {
            // 1) yaw around Z axis
            const rx = x * cy_ - y * sy_;
            const ry = x * sy_ + y * cy_;
            // 2) pitch around X axis (rotate the ry/z plane); negate Y so up is up
            const ry2 = ry * cp_ - z * sp_;
            return [rx, -ry2];
        };
        const [isoX, isoY] = rot(xVal, yVal, zVal);
        const corners = [
            [bbox.xMin, bbox.yMin, bbox.zMin], [bbox.xMax, bbox.yMin, bbox.zMin],
            [bbox.xMin, bbox.yMax, bbox.zMin], [bbox.xMax, bbox.yMax, bbox.zMin],
            [bbox.xMin, bbox.yMin, bbox.zMax], [bbox.xMax, bbox.yMin, bbox.zMax],
            [bbox.xMin, bbox.yMax, bbox.zMax], [bbox.xMax, bbox.yMax, bbox.zMax],
        ].map(([cx, cy, cz]) => {
            const [ix, iy] = rot(cx, cy, cz);
            return { ix, iy };
        });
        const xMin = Math.min(...corners.map(c => c.ix));
        const xMax = Math.max(...corners.map(c => c.ix));
        const yMin = Math.min(...corners.map(c => c.iy));
        const yMax = Math.max(...corners.map(c => c.iy));
        const rangeX = Math.max(1, xMax - xMin), rangeY = Math.max(1, yMax - yMin);
        const scale = Math.min((w - pad * 2) / rangeX, (h - pad * 2) / rangeY);
        // Center the projected bbox in the canvas — without this, when the
        // bbox aspect ratio doesn't match the canvas, the path hugs one edge.
        const usedW = rangeX * scale;
        const usedH = rangeY * scale;
        const offsetX = (w - usedW) / 2;
        const offsetY = (h - usedH) / 2;
        return {
            sx: offsetX + (isoX - xMin) * scale,
            sy: h - offsetY - (isoY - yMin) * scale,
        };
    };

    const zColor = (z, bbox) => {
        const range = Math.max(1, bbox.zMax - bbox.zMin);
        const t = (z - bbox.zMin) / range;
        if (t < 0.4) return '#1e40af';
        if (t < 0.7) return '#16a34a';
        return '#f59e0b';
    };

    const Render3D = ({ data, overlay, label, height = 400, width = 760, progress = 1, showCursor = false, compact = false, highlightLines = null, changeKind = 'change',
                        spotlightSegIdx = null,
                        syncedYaw, syncedPitch, syncedViewBox, onSyncChange }) => {
        // Fullscreen overlay state — when true, render fills the viewport
        const [isFullscreen, setIsFullscreen] = useState(false);
        // ESC to exit fullscreen
        useEffect(() => {
            if (!isFullscreen) return;
            const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
            window.addEventListener('keydown', onKey);
            return () => window.removeEventListener('keydown', onKey);
        }, [isFullscreen]);
        // Effective canvas dimensions — full viewport when maximized
        const w = isFullscreen ? Math.max(800, window.innerWidth - 60) : width;
        const h = isFullscreen ? Math.max(500, window.innerHeight - 140) : height;
        const safeData = data || { path: [], bbox: { xMin: 0, xMax: 100, yMin: 0, yMax: 100, zMin: 0, zMax: 10 } };
        const total = safeData.path.length;
        const visibleCount = Math.max(0, Math.min(total, Math.floor(progress * total)));
        const visiblePath = progress >= 1 ? safeData.path : safeData.path.slice(0, visibleCount);
        const isHighlighted = (seg) => highlightLines && seg && highlightLines.has(seg.line);

        // ===== Interactive viewport: drag to rotate, shift+drag to pan, wheel to zoom =====
        const initialViewBox = [0, 0, w, h];
        // If parent passes syncedX props + onSyncChange, we're CONTROLLED (shared
        // state across viewers). Otherwise we keep our own internal state.
        const [internalViewBox, setInternalViewBox] = useState(initialViewBox);
        const [internalYaw, setInternalYaw] = useState(30);
        const [internalPitch, setInternalPitch] = useState(60);
        const isControlled = typeof onSyncChange === 'function';
        const viewBox = isControlled && syncedViewBox ? syncedViewBox : internalViewBox;
        const userYaw = isControlled && syncedYaw != null ? syncedYaw : internalYaw;
        const userPitch = isControlled && syncedPitch != null ? syncedPitch : internalPitch;
        const setViewBox = (vb) => isControlled
            ? onSyncChange({ viewBox: typeof vb === 'function' ? vb(viewBox) : vb, yaw: userYaw, pitch: userPitch })
            : setInternalViewBox(vb);
        const setUserYaw = (yv) => isControlled
            ? onSyncChange({ viewBox, yaw: typeof yv === 'function' ? yv(userYaw) : yv, pitch: userPitch })
            : setInternalYaw(yv);
        const setUserPitch = (pv) => isControlled
            ? onSyncChange({ viewBox, yaw: userYaw, pitch: typeof pv === 'function' ? pv(userPitch) : pv })
            : setInternalPitch(pv);
        const [dragState, setDragState] = useState(null);
        const svgRef = React.useRef(null);
        // Reset viewport when data changes (new program loaded) — only when uncontrolled
        useEffect(() => { if (!isControlled) { setInternalViewBox([0, 0, w, h]); setInternalYaw(30); setInternalPitch(60); } }, [safeData, w, h, isControlled]);

        // Native wheel listener — only zooms when Ctrl (or Cmd on Mac) is held,
        // so a plain wheel scroll lets the page scroll normally past the viewer.
        useEffect(() => {
            const el = svgRef.current;
            if (!el) return;
            const handler = (e) => {
                if (!e.ctrlKey && !e.metaKey) return; // let the page scroll normally
                e.preventDefault();
                const rect = el.getBoundingClientRect();
                const mx = (e.clientX - rect.left) / rect.width;
                const my = (e.clientY - rect.top) / rect.height;
                const cur = viewBox;
                const [vx, vy, vw, vh] = cur;
                const factor = e.deltaY > 0 ? 1.15 : 0.87;
                const newW = Math.max(20, Math.min(w * 10, vw * factor));
                const newH = Math.max(20, Math.min(h * 10, vh * factor));
                setViewBox([vx + (vw - newW) * mx, vy + (vh - newH) * my, newW, newH]);
            };
            el.addEventListener('wheel', handler, { passive: false });
            return () => el.removeEventListener('wheel', handler);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [w, h, viewBox, userYaw, userPitch]);

        const onMouseDown = (e) => {
            if (e.button !== 0) return;
            setDragState({
                mode: e.shiftKey ? 'pan' : 'rotate',
                startX: e.clientX, startY: e.clientY,
                viewBox: [...viewBox], yawStart: userYaw, pitchStart: userPitch,
                rect: e.currentTarget.getBoundingClientRect(),
            });
        };
        const onMouseMove = (e) => {
            if (!dragState) return;
            if (dragState.mode === 'pan') {
                const [vx, vy, vw, vh] = dragState.viewBox;
                const dx = (e.clientX - dragState.startX) / dragState.rect.width * vw;
                const dy = (e.clientY - dragState.startY) / dragState.rect.height * vh;
                setViewBox([vx - dx, vy - dy, vw, vh]);
            } else {
                // rotate: horizontal drag = yaw, vertical drag = pitch (degrees)
                const dx = e.clientX - dragState.startX;
                const dy = e.clientY - dragState.startY;
                setUserYaw(dragState.yawStart + dx * 0.5);
                setUserPitch(Math.max(0, Math.min(90, dragState.pitchStart + dy * 0.5)));
            }
        };
        const onMouseUp = () => setDragState(null);
        const resetView = () => { setViewBox([0, 0, w, h]); setUserYaw(30); setUserPitch(60); };
        const zoomIn = () => { const [x, y, vw, vh] = viewBox; setViewBox([x + vw * 0.075, y + vh * 0.075, vw * 0.85, vh * 0.85]); };
        const zoomOut = () => { const [x, y, vw, vh] = viewBox; setViewBox([x - vw * 0.085, y - vh * 0.085, vw * 1.17, vh * 1.17]); };
        // Preset views — set absolute yaw + pitch + reset zoom/pan
        const setPresetView = (yawDeg, pitchDeg) => {
            setUserYaw(yawDeg);
            setUserPitch(pitchDeg);
            setViewBox([0, 0, w, h]);
        };
        // Effective rotation passed to project()
        const effYaw = userYaw;
        const effPitch = userPitch;

        // ===== Memoize the path-bucketing so animation frames don't rewalk
        // the entire segment array on every render =====
        const bucketsMemo = useMemo(() => {
            const buckets = [[], [], [], [], [], []];
            const highlighted = [];
            const zMin = safeData.bbox.zMin, zSpan = Math.max(1e-6, safeData.bbox.zMax - safeData.bbox.zMin);
            for (const p of visiblePath) {
                if (p.type === 'G0') continue;
                if (isHighlighted(p)) { highlighted.push(p); continue; }
                const zMid = (p.z1 + p.z2) / 2;
                const t = Math.max(0, Math.min(0.999, (zMid - zMin) / zSpan));
                const bi = Math.floor(t * buckets.length);
                const a = project(p.x1, p.y1, p.z1, safeData.bbox, w, h, effYaw, effPitch);
                const b = project(p.x2, p.y2, p.z2, safeData.bbox, w, h, effYaw, effPitch);
                buckets[bi].push(`M${a.sx.toFixed(1)} ${a.sy.toFixed(1)}L${b.sx.toFixed(1)} ${b.sy.toFixed(1)}`);
            }
            const ds = buckets.map(segs => segs.join(' '));
            const rapidD = [];
            const rapidHl = [];
            for (const p of visiblePath) {
                if (p.type !== 'G0') continue;
                if (isHighlighted(p)) { rapidHl.push(p); continue; }
                const a = project(p.x1, p.y1, p.z1, safeData.bbox, w, h, effYaw, effPitch);
                const b = project(p.x2, p.y2, p.z2, safeData.bbox, w, h, effYaw, effPitch);
                rapidD.push(`M${a.sx.toFixed(1)} ${a.sy.toFixed(1)}L${b.sx.toFixed(1)} ${b.sy.toFixed(1)}`);
            }
            return { cutD: ds, cutHl: highlighted, rapidD: rapidD.join(' '), rapidHl };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [safeData, visibleCount, w, h, effYaw, effPitch, highlightLines]);

        const overlayD = useMemo(() => {
            if (!overlay) return '';
            const overlayPath = overlay.path.slice(0, progress >= 1 ? overlay.path.length : Math.floor(progress * overlay.path.length));
            const d = [];
            for (const p of overlayPath) {
                if (p.type !== 'G0') continue;
                const a = project(p.x1, p.y1, p.z1, safeData.bbox, w, h, effYaw, effPitch);
                const b = project(p.x2, p.y2, p.z2, safeData.bbox, w, h, effYaw, effPitch);
                d.push(`M${a.sx.toFixed(1)} ${a.sy.toFixed(1)}L${b.sx.toFixed(1)} ${b.sy.toFixed(1)}`);
            }
            return d.join(' ');
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [overlay, progress, safeData.bbox, w, h, effYaw, effPitch]);

        // Hooks above always run; OK to bail out here for empty data
        if (!data || data.path.length === 0) return null;
        // Tool cursor at the last visible segment endpoint
        let cursor = null;
        if (showCursor && visibleCount > 0 && visibleCount <= total) {
            const last = visiblePath[visiblePath.length - 1];
            if (last) {
                const p = project(last.x2, last.y2, last.z2, data.bbox, w, h, effYaw, effPitch);
                cursor = { sx: p.sx, sy: p.sy, isRapid: last.type === 'G0', isChange: isHighlighted(last) };
            }
        }
        return (
            <div style={isFullscreen ? {
                position: 'fixed', inset: 0, zIndex: 2000,
                background: 'var(--y-bg-soft, #fafafa)', display: 'flex', flexDirection: 'column',
                padding: 12, overflow: 'auto',
            } : { background: 'var(--y-bg-soft, #fafafa)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 6 }}>
                <div style={{ padding: compact ? '6px 12px' : '10px 14px', fontSize: compact ? 13 : 13, fontWeight: 800, color: '#374151', borderBottom: '1px solid var(--y-border, #e5e7eb)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span>{label}{isFullscreen ? ' · FULLSCREEN' : ''}</span>
                    {!compact && (
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span><span style={{ display: 'inline-block', width: 14, height: 2, background: '#1e40af', verticalAlign: 'middle', marginRight: 4 }}></span>cut (deep→shallow Z)</span>
                            {overlay ? (
                                <>
                                    <span><span style={{ display: 'inline-block', width: 14, borderTop: '2px dashed #dc2626', verticalAlign: 'middle', marginRight: 4 }}></span>ORIGINAL rapid</span>
                                    <span><span style={{ display: 'inline-block', width: 14, borderTop: '2px solid #16a34a', verticalAlign: 'middle', marginRight: 4 }}></span>OPTIMIZED rapid</span>
                                </>
                            ) : (
                                <span><span style={{ display: 'inline-block', width: 14, borderTop: '2px dashed #dc2626', verticalAlign: 'middle', marginRight: 4 }}></span>rapid</span>
                            )}
                        </span>
                    )}
                    {compact && progress < 1 && (
                        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#7c3aed', fontFamily: 'monospace', fontWeight: 700 }}>
                            seg {visibleCount}/{total}
                        </span>
                    )}
                    {/* View presets + zoom + reset (hidden in compact mode) */}
                    {!compact && (
                        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span title={'Drag = rotate part\nShift + drag = pan\nCtrl + wheel = zoom\n(plain wheel scrolls page)'} style={{ fontSize: 10, color: '#6b7280', cursor: 'help', borderBottom: '1px dotted #9ca3af', padding: '0 4px' }}>
                                ℹ controls
                            </span>
                            <span style={{ display: 'inline-flex', gap: 2 }}>
                                {[
                                    // With project() doing yaw-around-Z then pitch-around-X:
                                    //   yaw 0,   pitch 0   → XY plane (looking down Z) = TOP
                                    //   yaw 0,   pitch 90  → XZ plane (looking along -Y) = FRONT
                                    //   yaw 180, pitch 90  → XZ plane mirrored = BACK
                                    //   yaw 90,  pitch 90  → YZ plane = SIDE
                                    //   yaw 30,  pitch 60  → isometric-ish 3D
                                    { l: 'Top', y: 0, p: 0 },
                                    { l: 'Front', y: 0, p: 90 },
                                    { l: 'Back', y: 180, p: 90 },
                                    { l: 'Side', y: 90, p: 90 },
                                    { l: 'Iso', y: 30, p: 60 },
                                ].map(v => {
                                    const isActive = Math.abs(userYaw - v.y) < 0.5 && Math.abs(userPitch - v.p) < 0.5;
                                    return (
                                        <button key={v.l} onClick={() => setPresetView(v.y, v.p)}
                                            title={`${v.l} view (yaw ${v.y}° · pitch ${v.p}°)`}
                                            style={{
                                                padding: '3px 8px', fontSize: 11, fontWeight: 700,
                                                background: isActive ? '#1e40af' : '#fff',
                                                color: isActive ? '#fff' : '#1e40af',
                                                border: '1px solid #1e40af', borderRadius: 3, cursor: 'pointer',
                                            }}>{v.l}</button>
                                    );
                                })}
                            </span>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'monospace' }}>
                                {Math.round(userYaw)}°/{Math.round(userPitch)}°
                            </span>
                            <button onClick={zoomOut} title="Zoom out" style={{ padding: '3px 8px', fontSize: 12, fontWeight: 700, background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #d1d5db)', borderRadius: 3, cursor: 'pointer' }}>−</button>
                            <button onClick={zoomIn} title="Zoom in" style={{ padding: '3px 8px', fontSize: 12, fontWeight: 700, background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #d1d5db)', borderRadius: 3, cursor: 'pointer' }}>+</button>
                            <button onClick={resetView} title="Reset view (rotation + zoom + pan)" style={{ padding: '3px 8px', fontSize: 11, fontWeight: 700, background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #d1d5db)', borderRadius: 3, cursor: 'pointer' }}>⟲</button>
                            <button
                                onClick={() => setIsFullscreen(f => !f)}
                                title={isFullscreen ? 'Exit fullscreen (Esc)' : 'View in fullscreen'}
                                style={{ padding: '3px 8px', fontSize: 12, fontWeight: 700, background: isFullscreen ? '#1e40af' : '#fff', color: isFullscreen ? '#fff' : '#1e40af', border: '1px solid #1e40af', borderRadius: 3, cursor: 'pointer' }}
                            >
                                {isFullscreen ? '⛶✕' : '⛶'}
                            </button>
                        </span>
                    )}
                </div>
                <svg ref={svgRef} width={w} height={h}
                    viewBox={viewBox.join(' ')}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    title="Drag to rotate · Shift+drag to pan · Wheel to zoom"
                    style={{ width: '100%', height: 'auto', cursor: dragState ? (dragState.mode === 'pan' ? 'grabbing' : 'crosshair') : 'grab', touchAction: 'none', userSelect: 'none' }}>
                    {/* Cuts: 6 Z-color buckets, each ONE <path> with combined d-string.
                        Memoized so animation frames don't rebuild these strings.
                        When the Yantra Intelligence-changes spotlight is active, DIM the background
                        cuts so the red/green change segments visually pop. */}
                    {bucketsMemo.cutD.map((d, bi) => d.length === 0 ? null : (
                        <path key={'c' + bi} d={d}
                            stroke={zColor(data.bbox.zMin + ((bi + 0.5) / 6) * (data.bbox.zMax - data.bbox.zMin), data.bbox)}
                            strokeWidth={1.4} opacity={spotlightSegIdx != null ? 0.18 : 0.85} fill="none" />
                    ))}
                    {bucketsMemo.cutHl.map((p, i) => {
                        const a = project(p.x1, p.y1, p.z1, data.bbox, w, h, effYaw, effPitch);
                        const b = project(p.x2, p.y2, p.z2, data.bbox, w, h, effYaw, effPitch);
                        const c = changeKind === 'remove' ? '#dc2626' : changeKind === 'add' ? '#16a34a' : '#7c3aed';
                        // When a spotlight is active, DIM all other change highlights
                        // so the user can focus on just the current change.
                        const isCurrent = spotlightSegIdx != null && safeData.path[spotlightSegIdx] === p;
                        const dim = spotlightSegIdx != null && !isCurrent;
                        return (
                            <line key={'hl' + i} x1={a.sx} y1={a.sy} x2={b.sx} y2={b.sy}
                                stroke={c} strokeWidth={isCurrent ? 4 : 3} opacity={dim ? 0.18 : 1} />
                        );
                    })}
                    {bucketsMemo.rapidD.length > 0 && (
                        <path d={bucketsMemo.rapidD} stroke="#dc2626"
                            strokeWidth={overlay ? 0.6 : 0.8}
                            strokeDasharray="4 3"
                            opacity={spotlightSegIdx != null ? 0.12 : (overlay ? 0.35 : 0.5)} fill="none" />
                    )}
                    {bucketsMemo.rapidHl.map((p, i) => {
                        const a = project(p.x1, p.y1, p.z1, data.bbox, w, h, effYaw, effPitch);
                        const b = project(p.x2, p.y2, p.z2, data.bbox, w, h, effYaw, effPitch);
                        const c = changeKind === 'remove' ? '#dc2626' : changeKind === 'add' ? '#16a34a' : '#7c3aed';
                        return (
                            <line key={'rhl' + i} x1={a.sx} y1={a.sy} x2={b.sx} y2={b.sy}
                                stroke={c} strokeWidth={2.5} opacity={1} />
                        );
                    })}
                    {overlayD.length > 0 && (
                        <path d={overlayD} stroke="#16a34a" strokeWidth={1.4} opacity={0.85} fill="none" />
                    )}
                    {/* Tool cursor */}
                    {cursor && (
                        <g>
                            {(() => {
                                // Color + label depend on change-kind: 'remove' (BEFORE), 'add' (AFTER), or generic
                                const changeColor = changeKind === 'remove' ? '#dc2626' : changeKind === 'add' ? '#16a34a' : '#7c3aed';
                                const changeText = changeKind === 'remove' ? '− Yantra Intelligence removed' : changeKind === 'add' ? '+ Yantra Intelligence added' : '⚡ Yantra Intelligence change';
                                const ringColor = cursor.isChange ? changeColor : (cursor.isRapid ? '#dc2626' : '#1e40af');
                                return (<>
                                    <circle cx={cursor.sx} cy={cursor.sy} r="8" fill="none" stroke={ringColor} strokeWidth="1.5" opacity="0.4">
                                        <animate attributeName="r" values="6;14;6" dur="1s" repeatCount="indefinite" />
                                        <animate attributeName="opacity" values="0.9;0.1;0.9" dur="1s" repeatCount="indefinite" />
                                    </circle>
                                    <circle cx={cursor.sx} cy={cursor.sy} r="5" fill={ringColor} />
                                    {cursor.isChange && (
                                        <text x={cursor.sx + 10} y={cursor.sy - 8} fontSize="11" fontWeight="800" fill={changeColor}>{changeText}</text>
                                    )}
                                </>);
                            })()}
                        </g>
                    )}
                    {/* Spotlight marker — used by "Play Yantra Intelligence changes" so the full
                        program stays rendered and a big pulsing ring moves to
                        each change segment in turn. Dims everything else. */}
                    {spotlightSegIdx != null && safeData.path[spotlightSegIdx] && (() => {
                        const s = safeData.path[spotlightSegIdx];
                        const p = project(s.x2, s.y2, s.z2, safeData.bbox, w, h, effYaw, effPitch);
                        const color = changeKind === 'remove' ? '#dc2626' : changeKind === 'add' ? '#16a34a' : '#7c3aed';
                        const label = changeKind === 'remove' ? '− Yantra Intelligence removed' : changeKind === 'add' ? '+ Yantra Intelligence added' : '⚡ change';
                        return (
                            <g>
                                {/* Soft halo behind the spot, helps the eye find it in a busy view */}
                                <circle cx={p.sx} cy={p.sy} r="32" fill={color} opacity="0.08" />
                                <circle cx={p.sx} cy={p.sy} r="14" fill="none" stroke={color} strokeWidth="3" opacity="0.6">
                                    <animate attributeName="r" values="10;28;10" dur="1.2s" repeatCount="indefinite" />
                                    <animate attributeName="opacity" values="1;0.1;1" dur="1.2s" repeatCount="indefinite" />
                                </circle>
                                <circle cx={p.sx} cy={p.sy} r="6" fill={color} stroke="#fff" strokeWidth="1.5" />
                                <text x={p.sx + 14} y={p.sy - 12} fontSize="12" fontWeight="800" fill={color} stroke="#fff" strokeWidth="0.5" paintOrder="stroke">{label}</text>
                            </g>
                        );
                    })()}
                </svg>
            </div>
        );
    };

    // ===== NC Player: BEFORE / AFTER side-by-side with synchronized playback =====
    const NcPlayer = ({ before, after, originalText, optimizedText, beforeLabel = 'BEFORE (original)', afterLabel = 'AFTER (Yantra Intelligence-optimized)' }) => {
        const [progress, setProgress] = useState(1);   // 0..1
        const [playing, setPlaying] = useState(false);
        const [speed, setSpeed] = useState(1);          // multiplier: 1x = 10s full path
        const rafRef = React.useRef(null);
        const lastTickRef = React.useRef(null);
        // Shared camera state — both viewers display the same yaw/pitch/viewBox
        // so dragging/zooming one mirrors the other.
        const [sharedView, setSharedView] = useState({ yaw: 30, pitch: 60, viewBox: [0, 0, 440, 360] });

        const totalBefore = before?.path?.length || 0;
        const totalAfter = after?.path?.length || 0;
        const segCountForCounter = Math.max(totalBefore, totalAfter);
        // Animation is too heavy for huge programs (re-buckets thousands of
        // segments per frame in TWO viewers). Auto-disable above this threshold.
        // Animation is enabled up to a very large limit (50K segs). Programs
        // bigger than the "comfortable" threshold (15K) get a throttled animation
        // (15fps instead of 60fps) to keep the browser responsive.
        const ANIMATION_SEG_LIMIT = 50000;
        const ANIMATION_COMFORTABLE = 15000;
        // Also disable animation while the Yantra Intelligence is running — heavy raf re-renders
        // on top of a 5-min background fetch were causing visible browser hangs.
        const animationDisabled = segCountForCounter > ANIMATION_SEG_LIMIT || aiCodeLoading || aiLoading;
        const animationThrottled = !animationDisabled && segCountForCounter > ANIMATION_COMFORTABLE;

        // ===== Detect changed source lines via diff, then map to segment indices =====
        const { beforeChangedLines, afterChangedLines, beforeChangeSegs, afterChangeSegs } = useMemo(() => {
            const empty = { beforeChangedLines: new Set(), afterChangedLines: new Set(), beforeChangeSegs: [], afterChangeSegs: [] };
            if (!originalText || !optimizedText) return empty;
            const d = diffLines(originalText, optimizedText);
            const bSet = new Set(), aSet = new Set();
            for (const s of d.segments) {
                if (s.type === '-' && s.aIdx != null) bSet.add(s.aIdx + 1);
                if (s.type === '+' && s.bIdx != null) aSet.add(s.bIdx + 1);
            }
            const beforeSegs = before?.path?.map((p, i) => bSet.has(p.line) ? i : -1).filter(i => i >= 0) || [];
            const afterSegs = after?.path?.map((p, i) => aSet.has(p.line) ? i : -1).filter(i => i >= 0) || [];
            return { beforeChangedLines: bSet, afterChangedLines: aSet, beforeChangeSegs: beforeSegs, afterChangeSegs: afterSegs };
        }, [originalText, optimizedText, before, after]);
        const geometryChanged = beforeChangeSegs.length > 0 || afterChangeSegs.length > 0;
        // The player iterates afterChangeSegs (the "Showing N/M" counter at
        // the right also uses this), so the Play-button label MUST use the
        // same number — otherwise we get "Play 12 changes" but the counter
        // ends at 6/6. Was using Math.max(before, after) which over-counted.
        const changeCount = afterChangeSegs.length;

        // PROGRESSIVE HIGHLIGHTS — while playing Yantra Intelligence changes, only highlight the
        // changes the player has reached so far. Each tick reveals one more
        // red/green-bold segment so the supervisor sees the Yantra Intelligence "applying"
        // changes one at a time instead of all 48 being visible from the start.
        // When not playing → show full highlight set (static review mode).

        // Jump to next/prev Yantra Intelligence change: use the spotlight pattern — keep progress
        // at 1 (full program visible) and just advance currentChangePos so the
        // spotlight marker moves to the new change. NEVER set progress to a
        // small fraction (that slices the path and breaks the visual).
        const jumpToNextChange = () => {
            setPlaying(false); setPlayingChanges(false);
            if (afterChangeSegs.length === 0) return;
            setProgress(1);
            setCurrentChangePos((p) => (p + 1) % afterChangeSegs.length);
        };
        const jumpToPrevChange = () => {
            setPlaying(false); setPlayingChanges(false);
            if (afterChangeSegs.length === 0) return;
            setProgress(1);
            setCurrentChangePos((p) => (p - 1 + afterChangeSegs.length) % afterChangeSegs.length);
        };

        // === Play Yantra Intelligence changes ===
        // Walks through every change segment one at a time with a brief dwell.
        // Lightweight — only updates `progress` to each change's index, doesn't
        // re-bucket the whole path per frame. Works even when full-program
        // animation is disabled for huge programs.
        const [playingChanges, setPlayingChanges] = useState(false);
        const [currentChangePos, setCurrentChangePos] = useState(0);
        // Dwell time per change (ms). Smaller = faster. Default = 1500ms.
        const [changeDwellMs, setChangeDwellMs] = useState(1500);
        // Spotlight shows whenever we have changes + a valid pointer (auto-play
        // OR manual Previous/Next). Always-visible spotlight makes the manual
        // stepping useful even when not playing.
        const _showSpotlight = afterChangeSegs.length > 0 && currentChangePos >= 0 && currentChangePos < afterChangeSegs.length;

        // Look up the N-operation header (e.g. "N18(DIA 20MM COROMILL)") that
        // owns a given source line. Scans backwards from the line to the
        // nearest preceding "N<num>(<name>)" comment.
        const findOperation = (text, lineNo) => {
            if (!text || !lineNo) return null;
            const lines = text.split('\n');
            const start = Math.min(lineNo - 1, lines.length - 1);
            for (let i = start; i >= 0; i--) {
                const m = lines[i].match(/^\s*(N\d+)\s*\(([^)]+)\)/);
                if (m) return { tag: m[1], name: m[2].trim() };
            }
            return null;
        };
        const currentOperation = useMemo(() => {
            if (!_showSpotlight) return null;
            const seg = after?.path?.[afterChangeSegs[currentChangePos]];
            if (!seg) return null;
            return findOperation(optimizedText, seg.line);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [_showSpotlight, currentChangePos, afterChangeSegs, after, optimizedText]);
        const spotlightBeforeSeg = _showSpotlight && beforeChangeSegs.length > currentChangePos ? beforeChangeSegs[currentChangePos] : null;
        const spotlightAfterSeg = _showSpotlight && afterChangeSegs.length > currentChangePos ? afterChangeSegs[currentChangePos] : null;

        // Build progressive highlight sets — line numbers of changes [0..currentChangePos].
        // showAllChanges flag overrides this for users who want the static "all
        // 48 changes visible at once" view (toggled via "Show All" button).
        // Reset puts currentChangePos back to -1 (NO highlights, completely clean).
        const [showAllChanges, setShowAllChanges] = useState(true);
        const progressiveBeforeLines = useMemo(() => {
            if (showAllChanges) return beforeChangedLines;
            const s = new Set();
            // currentChangePos === -1 means reset/cleared (show nothing)
            const upTo = currentChangePos;
            for (let i = 0; i <= upTo && i < beforeChangeSegs.length; i++) {
                const seg = before?.path?.[beforeChangeSegs[i]];
                if (seg?.line != null) s.add(seg.line);
            }
            return s;
        }, [showAllChanges, currentChangePos, beforeChangeSegs, before, beforeChangedLines]);
        const progressiveAfterLines = useMemo(() => {
            if (showAllChanges) return afterChangedLines;
            const s = new Set();
            const upTo = currentChangePos;
            for (let i = 0; i <= upTo && i < afterChangeSegs.length; i++) {
                const seg = after?.path?.[afterChangeSegs[i]];
                if (seg?.line != null) s.add(seg.line);
            }
            return s;
        }, [showAllChanges, currentChangePos, afterChangeSegs, after, afterChangedLines]);

        // Reset: clear all highlights, stop playback, position to -1 (no spotlight either)
        const resetChangePlayback = () => {
            setPlayingChanges(false);
            setShowAllChanges(false);
            setCurrentChangePos(-1);
        };

        useEffect(() => {
            if (!playingChanges || afterChangeSegs.length === 0) return;
            const t = setTimeout(() => {
                const nextPos = currentChangePos + 1;
                if (nextPos >= afterChangeSegs.length) {
                    setPlayingChanges(false);
                } else {
                    setCurrentChangePos(nextPos);
                    // progress stays at 1 — don't slice the path
                }
            }, changeDwellMs);
            return () => clearTimeout(t);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [playingChanges, currentChangePos, afterChangeSegs.length, changeDwellMs]);

        const playAIChanges = () => {
            if (afterChangeSegs.length === 0) return;
            setPlaying(false);
            setProgress(1); // show the full program, spotlight does the moving
            // Always restart from the first change AND enable progressive reveal
            // so highlights appear one at a time as the spotlight walks through.
            setShowAllChanges(false);
            setCurrentChangePos(0);
            setPlayingChanges(true);
        };
        const stopChangePlayback = () => setPlayingChanges(false);

        // Animation loop using requestAnimationFrame — disabled only for truly
        // huge programs (>50K segs). For 15K–50K, we throttle to ~15fps so the
        // browser stays responsive while still showing visible animation.
        useEffect(() => {
            if (!playing || animationDisabled) return;
            const FULL_DURATION_MS = 10000 / Math.max(0.1, speed);
            const minFrameInterval = animationThrottled ? 66 : 0; // ~15fps for big programs
            let lastFrameTs = 0;
            const step = (ts) => {
                if (lastTickRef.current == null) lastTickRef.current = ts;
                const dt = ts - lastTickRef.current;
                if (ts - lastFrameTs < minFrameInterval) {
                    rafRef.current = requestAnimationFrame(step);
                    return;
                }
                lastFrameTs = ts;
                lastTickRef.current = ts;
                setProgress((p) => {
                    const next = p + dt / FULL_DURATION_MS;
                    if (next >= 1) { setPlaying(false); return 1; }
                    return next;
                });
                rafRef.current = requestAnimationFrame(step);
            };
            rafRef.current = requestAnimationFrame(step);
            return () => {
                if (rafRef.current) cancelAnimationFrame(rafRef.current);
                lastTickRef.current = null;
            };
        }, [playing, speed, animationDisabled, animationThrottled]);

        const togglePlay = () => {
            // Always restart from 0 when starting playback (don't resume from
            // mid-progress where user previously paused). Toggle stops if
            // currently playing.
            if (!playing) {
                setProgress(0);
                lastTickRef.current = null;
            }
            setPlaying((p) => !p);
        };
        const reset = () => { setPlaying(false); setProgress(0); };
        const finish = () => { setPlaying(false); setProgress(1); };

        if (!before || !after) return null;

        return (
            <div style={{ background: 'var(--y-bg, #fff)', border: '2px solid #1e40af', borderRadius: 10, padding: 16, marginBottom: 14 }}>
                {/* Title row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#1e40af' }}>
                        {animationDisabled ? '📐 Toolpath — BEFORE vs AFTER (static)' : '🎬 Toolpath Animation — BEFORE vs AFTER'}
                    </div>
                    {animationDisabled && (
                        <span style={{ fontSize: 11, color: '#92400e', background: '#fffbeb', padding: '2px 8px', borderRadius: 999, border: '1px solid #fcd34d' }}
                            title={`Program has ${segCountForCounter.toLocaleString()} segments — animation disabled to keep the browser responsive (threshold ${ANIMATION_SEG_LIMIT.toLocaleString()})`}>
                            animation off · {segCountForCounter.toLocaleString()} segs
                        </span>
                    )}
                    {animationThrottled && !animationDisabled && (
                        <span style={{ fontSize: 11, color: '#1e40af', background: '#eff6ff', padding: '2px 8px', borderRadius: 999, border: '1px solid #93c5fd' }}
                            title={`Program has ${segCountForCounter.toLocaleString()} segments — animation throttled to 15fps for smooth playback`}>
                            ⚡ perf mode · {segCountForCounter.toLocaleString()} segs
                        </span>
                    )}
                    {geometryChanged ? (
                        <span style={{ fontSize: 12, fontWeight: 800, background: '#7c3aed', color: '#fff', padding: '3px 10px', borderRadius: 999 }}>
                            ⚡ {changeCount} segment{changeCount === 1 ? '' : 's'} changed
                        </span>
                    ) : (
                        <span style={{ fontSize: 12, fontWeight: 800, background: '#dcfce7', color: '#15803d', padding: '3px 10px', borderRadius: 999 }} title="The Yantra Intelligence changed code structure (M-code merges, formatting) but did NOT alter cutter positions. This is safe behaviour — geometry is preserved.">
                            ✓ Geometry preserved
                        </span>
                    )}
                    <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280', fontFamily: 'monospace', fontWeight: 700 }}>
                        seg {Math.floor(progress * segCountForCounter)} / {segCountForCounter} <span style={{ color: '#9ca3af' }}>({Math.round(progress * 100)}%)</span>
                    </span>
                </div>

                {/* Parser scope hint — collapsed to a small ℹ icon, click to expand */}
                <details style={{ marginBottom: 8 }}>
                    <summary style={{ fontSize: 11, color: '#9ca3af', cursor: 'pointer', userSelect: 'none' }}>
                        ℹ️ Parser scope details
                    </summary>
                    <div style={{ fontSize: 11, color: '#6b7280', padding: '8px 12px', marginTop: 4, background: 'var(--y-bg-soft, #fafafa)', borderRadius: 4, lineHeight: 1.5 }}>
                        Expands: motion (G0/G1/G2/G3), macro variables (#NNN), work offsets (G54-G59, G10L2), canned cycles (G73/G81/G82/G83/G84/G85/G86/G89), WHILE loops. G65/G66 macro subprograms are not expanded since they live on the machine controller, not in the .nc file.
                    </div>
                </details>

                {/* Playback controls row 1: transport + scrubber — always shown
                    for UI consistency. When animation is disabled (huge program),
                    Play button is dimmed with explanation. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '10px 14px', background: 'var(--y-bg-soft, #f8fafc)', borderRadius: 6, flexWrap: 'wrap', opacity: animationDisabled ? 0.55 : 1 }}>
                    <button
                        onClick={togglePlay}
                        disabled={animationDisabled}
                        title={animationDisabled
                            ? `Path animation disabled — program has ${segCountForCounter.toLocaleString()} segments (threshold ${ANIMATION_SEG_LIMIT.toLocaleString()}). Use the Yantra Intelligence changes player below instead.`
                            : (playing ? 'Pause' : (progress >= 1 ? 'Replay from start' : 'Play'))}
                        style={{
                            padding: '8px 16px', fontSize: 13, fontWeight: 700,
                            background: animationDisabled ? '#9ca3af' : (playing ? '#dc2626' : '#16a34a'),
                            color: '#fff', border: 'none', borderRadius: 4,
                            cursor: animationDisabled ? 'not-allowed' : 'pointer', minWidth: 96,
                        }}
                    >
                        {playing ? '⏸ Pause' : (progress >= 1 ? '↻ Replay' : '▶ Play')}
                    </button>
                    <button onClick={reset} disabled={animationDisabled} title="Reset to start" style={{ padding: '8px 12px', fontSize: 13, background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #d1d5db)', borderRadius: 4, cursor: animationDisabled ? 'not-allowed' : 'pointer' }}>⏮</button>
                    <button onClick={finish} disabled={animationDisabled} title="Skip to end" style={{ padding: '8px 12px', fontSize: 13, background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #d1d5db)', borderRadius: 4, cursor: animationDisabled ? 'not-allowed' : 'pointer' }}>⏭</button>
                    <input
                        type="range" min="0" max="1000" value={Math.round(progress * 1000)}
                        onChange={(e) => { setPlaying(false); setProgress(Number(e.target.value) / 1000); }}
                        disabled={animationDisabled}
                        style={{ flex: 1, accentColor: '#1e40af', minWidth: 200, height: 6, cursor: animationDisabled ? 'not-allowed' : 'pointer' }}
                    />
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, marginRight: 4 }}>SPEED</span>
                        {[0.5, 1, 2, 5].map(s => (
                            <button key={s} onClick={() => setSpeed(s)} disabled={animationDisabled} style={{
                                padding: '6px 10px', fontSize: 12, fontWeight: 700,
                                background: speed === s ? '#1e40af' : '#fff', color: speed === s ? '#fff' : '#1e40af',
                                border: '1px solid #1e40af', borderRadius: 3, cursor: animationDisabled ? 'not-allowed' : 'pointer', minWidth: 36,
                            }}>{s}×</button>
                        ))}
                    </div>
                    {animationDisabled && (
                        <span style={{ fontSize: 11, color: '#9a3412', fontStyle: 'italic', flexBasis: '100%', marginTop: 4 }}>
                            ℹ Full-path animation disabled for very large programs ({segCountForCounter.toLocaleString()} segs &gt; {ANIMATION_SEG_LIMIT.toLocaleString()}). Use the <strong>Yantra Intelligence changes</strong> player below to step through changes.
                        </span>
                    )}
                    {animationThrottled && !animationDisabled && (
                        <span style={{ fontSize: 11, color: '#1e40af', fontStyle: 'italic', flexBasis: '100%', marginTop: 4 }}>
                            ⚡ Performance mode active ({segCountForCounter.toLocaleString()} segs) — animation runs at ~15fps to keep the browser smooth. Use the <strong>Yantra Intelligence changes</strong> player below for crisper change-by-change review.
                        </span>
                    )}
                </div>

                {/* Playback controls row 2: change-navigation (always shown when changes exist,
                    including in animation-disabled mode so users can still step through Yantra Intelligence changes) */}
                {geometryChanged && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '8px 14px', background: '#faf5ff', borderRadius: 6, border: '1px solid #ddd6fe', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, color: '#5b21b6', fontWeight: 700 }}>Yantra Intelligence changes:</span>
                        <button
                            onClick={playingChanges ? stopChangePlayback : playAIChanges}
                            title={playingChanges ? 'Stop playback' : 'Auto-play through every Yantra Intelligence change with a brief dwell at each'}
                            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, background: playingChanges ? '#dc2626' : '#16a34a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                        >
                            {playingChanges ? '⏸ Stop' : `▶ Play ${changeCount} changes`}
                        </button>
                        <button onClick={jumpToPrevChange} title="Previous Yantra Intelligence change" style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, background: 'var(--y-bg, #fff)', border: '1px solid #7c3aed', color: '#7c3aed', borderRadius: 4, cursor: 'pointer' }}>⏮ Previous</button>
                        <button onClick={jumpToNextChange} title="Next Yantra Intelligence change — pauses at the line where the Yantra Intelligence modified geometry" style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, background: '#7c3aed', border: '1px solid #7c3aed', color: '#fff', borderRadius: 4, cursor: 'pointer' }}>Next ⏭</button>
                        <button
                            onClick={resetChangePlayback}
                            title="Clear all highlights — go back to a clean canvas. Then click Play to start fresh."
                            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, background: 'var(--y-bg, #fff)', border: '1px solid #6b7280', color: '#374151', borderRadius: 4, cursor: 'pointer' }}
                        >↺ Reset</button>
                        <button
                            onClick={() => { setPlayingChanges(false); setShowAllChanges(true); setCurrentChangePos(changeCount - 1); }}
                            title="Show every Yantra Intelligence change highlighted at once (static review mode)"
                            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, background: showAllChanges ? '#7c3aed' : '#fff', border: '1px solid #7c3aed', color: showAllChanges ? '#fff' : '#7c3aed', borderRadius: 4, cursor: 'pointer' }}
                        >👁 Show All</button>
                        {/* Speed picker — dwell time per change during auto-play */}
                        <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center', marginLeft: 8 }}>
                            <span style={{ fontSize: 10, color: '#5b21b6', fontWeight: 700, marginRight: 4 }}>SPEED</span>
                            {[{l:'0.5x',v:3000},{l:'1x',v:1500},{l:'2x',v:750},{l:'5x',v:300}].map(opt => (
                                <button key={opt.l} onClick={() => setChangeDwellMs(opt.v)}
                                    title={`${opt.v}ms dwell at each change`}
                                    style={{
                                        padding: '4px 8px', fontSize: 11, fontWeight: 700,
                                        background: changeDwellMs === opt.v ? '#7c3aed' : '#fff',
                                        color: changeDwellMs === opt.v ? '#fff' : '#7c3aed',
                                        border: '1px solid #7c3aed', borderRadius: 3, cursor: 'pointer', minWidth: 32,
                                    }}>{opt.l}</button>
                            ))}
                        </span>
                        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#5b21b6', display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span>
                                Showing change <strong>{currentChangePos + 1} / {afterChangeSegs.length}</strong>
                                {playingChanges && <span style={{ marginLeft: 6, color: '#16a34a', fontWeight: 800 }}>▶ playing</span>}
                            </span>
                            {currentOperation && (
                                <span title="Operation/tool block that contains the currently-spotlit change"
                                    style={{ padding: '3px 10px', background: 'var(--y-bg, #fff)', border: '1px solid #ddd6fe', borderRadius: 999, color: '#5b21b6', fontWeight: 700 }}>
                                    <span style={{ color: '#7c3aed', fontFamily: 'monospace' }}>{currentOperation.tag}</span> · {currentOperation.name}
                                </span>
                            )}
                        </span>
                    </div>
                )}

                {/* Side-by-side viewers */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <Render3D data={before} label={beforeLabel} height={360} width={440} progress={progress} showCursor compact highlightLines={progressiveBeforeLines} changeKind="remove"
                        spotlightSegIdx={spotlightBeforeSeg}
                        syncedYaw={sharedView.yaw} syncedPitch={sharedView.pitch} syncedViewBox={sharedView.viewBox}
                        onSyncChange={(v) => setSharedView(v)} />
                    <Render3D data={after} label={afterLabel} height={360} width={440} progress={progress} showCursor compact highlightLines={progressiveAfterLines} changeKind="add"
                        spotlightSegIdx={spotlightAfterSeg}
                        syncedYaw={sharedView.yaw} syncedPitch={sharedView.pitch} syncedViewBox={sharedView.viewBox}
                        onSyncChange={(v) => setSharedView(v)} />
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 10, lineHeight: 1.6 }}>
                    💡 <strong>Synced view:</strong> drag / pan / zoom on EITHER viewer — both mirror the same camera. Controls: <strong>drag</strong>=rotate · <strong>shift+drag</strong>=pan · <strong>ctrl+wheel</strong>=zoom (plain wheel scrolls the page).<br />
                    Yantra Intelligence-touched segments: <span style={{ color: '#dc2626', fontWeight: 700 }}>red bold = Yantra Intelligence REMOVED</span> in BEFORE · <span style={{ color: '#16a34a', fontWeight: 700 }}>green bold = Yantra Intelligence ADDED</span> in AFTER. Tool dot: <span style={{ color: '#dc2626', fontWeight: 700 }}>red</span>=rapid · <span style={{ color: '#1e40af', fontWeight: 700 }}>blue</span>=cut.
                </div>
            </div>
        );
    };

    // ===== Visual design helpers (numbered badge + chip) =====
    const SectionBadge = ({ n, title, sub }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
                width: 26, height: 26, borderRadius: 6, background: '#F3951D', color: '#fff',
                fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
                letterSpacing: 0.5,
            }}>{String(n).padStart(2, '0')}</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{title}</div>
            {sub && <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>· {sub}</div>}
        </div>
    );
    const Chip = ({ children, bg = '#dcfce7', tx = '#15803d', dot = '#22c55e' }) => (
        <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
            background: bg, color: tx, borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
        }}>
            {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, display: 'inline-block' }} />}
            {children}
        </div>
    );

    return (
        <div style={{ padding: '20px 28px 40px', background: 'var(--y-bg-soft, #fafafa)', minHeight: 'calc(100vh - 70px)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: '0 4px' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', letterSpacing: -0.3 }}>G-Code Optimizer</div>
                <div style={{ marginLeft: 'auto', fontSize: 12, color: '#9ca3af', fontFamily: 'monospace' }}>
                    3D analysis · Yantra Intelligence suggestions · toolpath optimization
                </div>
            </div>

            {/* === MACHINE PROGRAM SELECTOR — with By Machine / By Program toggle === */}
            <div style={{ background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>📡 Program Library</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>
                        {viewMode === 'machine'
                            ? (programsLoading ? 'loading…' : `${programs.length} machine${programs.length === 1 ? '' : 's'} synced`)
                            : (uniqueLoading ? 'loading…' : `${uniquePrograms.length} unique program${uniquePrograms.length === 1 ? '' : 's'} in last 7 days`)
                        }
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'inline-flex', background: '#f3f4f6', borderRadius: 999, padding: 3 }}>
                        <button onClick={() => setViewMode('machine')} style={{
                            padding: '5px 14px', fontSize: 12, fontWeight: 700,
                            background: viewMode === 'machine' ? '#fff' : 'transparent',
                            color: viewMode === 'machine' ? '#111827' : '#6b7280',
                            border: 'none', borderRadius: 999, cursor: 'pointer',
                            boxShadow: viewMode === 'machine' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                        }}>By Machine</button>
                        <button onClick={() => setViewMode('program')} style={{
                            padding: '5px 14px', fontSize: 12, fontWeight: 700,
                            background: viewMode === 'program' ? '#fff' : 'transparent',
                            color: viewMode === 'program' ? '#111827' : '#6b7280',
                            border: 'none', borderRadius: 999, cursor: 'pointer',
                            boxShadow: viewMode === 'program' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                        }}>By Program</button>
                    </div>
                </div>
                {viewMode === 'machine' && programs.length === 0 && !programsLoading && (
                    <div style={{ fontSize: 12, color: '#6b7280', padding: 8 }}>
                        No G-code programs available from your machines yet.
                    </div>
                )}
                {viewMode === 'machine' && programs.length > 0 && (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 320 }}>
                            <InputLabel>Pick a machine</InputLabel>
                            <Select
                                value={selectedDevice}
                                label="Pick a machine"
                                onChange={(e) => loadFromMachine(e.target.value)}
                            >
                                {programs.map(p => (
                                    <MenuItem key={p.device_id} value={p.device_id}>
                                        <span style={{ fontWeight: 700 }}>{p.device_name}</span>
                                        {p.program_no && (
                                            <span style={{ marginLeft: 8, fontSize: 11, fontFamily: 'monospace', color: '#7c3aed', fontWeight: 700 }}>
                                                {p.program_no}
                                            </span>
                                        )}
                                        <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280' }}>
                                            {fmtSize(p.size)} · {fmtAgo(p.ts)}
                                        </span>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {loadingProgram && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#1e40af' }}>
                                <CircularProgress size={14} /> loading program…
                            </span>
                        )}
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {programs.slice(0, 6).map(p => (
                                <button key={p.device_id} onClick={() => loadFromMachine(p.device_id)}
                                    style={{
                                        padding: '4px 10px', fontSize: 11, fontWeight: 700,
                                        background: selectedDevice === p.device_id ? '#1e40af' : '#fff',
                                        color: selectedDevice === p.device_id ? '#fff' : '#374151',
                                        border: '1px solid ' + (selectedDevice === p.device_id ? '#1e40af' : '#d1d5db'),
                                        borderRadius: 14, cursor: 'pointer',
                                    }}>
                                    {p.device_name} · {fmtSize(p.size)}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {viewMode === 'program' && (() => {
                    // Derive the machine universe from the full unique set so the
                    // filter dropdown lists every machine that ever ran a program.
                    const allMachines = Array.from(new Set(uniquePrograms.flatMap(u => u.machines))).sort();
                    const q = progSearch.trim().toLowerCase();
                    let list = uniquePrograms.filter(u => {
                        if (progMinLoads > 0 && u.times_loaded < progMinLoads) return false;
                        if (progMachineFilter.length > 0 && !u.machines.some(m => progMachineFilter.includes(m))) return false;
                        if (q) {
                            const hay = (u.program_no + ' ' + u.machines.join(' ')).toLowerCase();
                            if (!hay.includes(q)) return false;
                        }
                        return true;
                    });
                    if (progSort === 'recent')   list = list.slice().sort((a,b) => b.last_seen - a.last_seen);
                    if (progSort === 'loads')    list = list.slice().sort((a,b) => b.times_loaded - a.times_loaded);
                    if (progSort === 'program')  list = list.slice().sort((a,b) => a.program_no.localeCompare(b.program_no));
                    if (progSort === 'machines') list = list.slice().sort((a,b) => b.machine_count - a.machine_count);
                    return (
                    <div>
                        {/* Filter bar */}
                        {!uniqueLoading && uniquePrograms.length > 0 && (
                            <div style={{
                                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
                                marginBottom: 10, padding: '8px 10px',
                                background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
                            }}>
                                <input
                                    type="text"
                                    value={progSearch}
                                    onChange={(e) => setProgSearch(e.target.value)}
                                    placeholder="🔍 Search program no. or machine…"
                                    style={{
                                        flex: '1 1 220px', minWidth: 200, padding: '6px 10px',
                                        fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6,
                                        background: '#fff', outline: 'none',
                                    }}
                                />
                                <select
                                    value={progSort}
                                    onChange={(e) => setProgSort(e.target.value)}
                                    style={{
                                        padding: '6px 10px', fontSize: 12, fontWeight: 600,
                                        border: '1px solid #d1d5db', borderRadius: 6, background: '#fff',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <option value="recent">Sort: Recent</option>
                                    <option value="loads">Sort: Most loaded</option>
                                    <option value="machines">Sort: Most machines</option>
                                    <option value="program">Sort: Program no.</option>
                                </select>
                                <select
                                    value={progMinLoads}
                                    onChange={(e) => setProgMinLoads(Number(e.target.value))}
                                    style={{
                                        padding: '6px 10px', fontSize: 12, fontWeight: 600,
                                        border: '1px solid #d1d5db', borderRadius: 6, background: '#fff',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <option value={0}>Min loads: any</option>
                                    <option value={2}>Min loads: 2+</option>
                                    <option value={5}>Min loads: 5+</option>
                                    <option value={10}>Min loads: 10+</option>
                                </select>
                                {/* Machine multi-select via chips */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                                    <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 700 }}>Machine:</span>
                                    {allMachines.map(m => {
                                        const active = progMachineFilter.includes(m);
                                        return (
                                            <button
                                                key={m}
                                                onClick={() => setProgMachineFilter(
                                                    active
                                                        ? progMachineFilter.filter(x => x !== m)
                                                        : [...progMachineFilter, m]
                                                )}
                                                style={{
                                                    padding: '3px 8px', fontSize: 10, fontWeight: 700,
                                                    fontFamily: 'monospace', cursor: 'pointer',
                                                    background: active ? '#3730a3' : '#eef2ff',
                                                    color: active ? '#fff' : '#3730a3',
                                                    border: '1px solid ' + (active ? '#3730a3' : '#c7d2fe'),
                                                    borderRadius: 4,
                                                }}
                                            >{m}</button>
                                        );
                                    })}
                                    {progMachineFilter.length > 0 && (
                                        <button
                                            onClick={() => setProgMachineFilter([])}
                                            style={{
                                                padding: '3px 8px', fontSize: 10, fontWeight: 700,
                                                background: '#fff', color: '#6b7280',
                                                border: '1px dashed #d1d5db', borderRadius: 4, cursor: 'pointer',
                                            }}
                                        >clear</button>
                                    )}
                                </div>
                                <div style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280', fontWeight: 700 }}>
                                    {list.length} of {uniquePrograms.length}
                                </div>
                            </div>
                        )}

                        {uniqueLoading && (
                            <div style={{ fontSize: 12, color: '#6b7280', padding: 8 }}>Loading unique programs…</div>
                        )}
                        {!uniqueLoading && uniquePrograms.length === 0 && (
                            <div style={{ fontSize: 12, color: '#6b7280', padding: 8 }}>
                                No program-load events in the last 7 days.
                            </div>
                        )}
                        {!uniqueLoading && uniquePrograms.length > 0 && list.length === 0 && (
                            <div style={{ fontSize: 12, color: '#6b7280', padding: 16, textAlign: 'center', fontStyle: 'italic' }}>
                                No programs match your filters. <button onClick={() => { setProgSearch(''); setProgMachineFilter([]); setProgMinLoads(0); }} style={{ marginLeft: 6, padding: '2px 8px', background: '#fff', color: '#3730a3', border: '1px solid #c7d2fe', borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>reset</button>
                            </div>
                        )}
                        {!uniqueLoading && list.length > 0 && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10, maxHeight: 360, overflow: 'auto', padding: 4 }}>
                                {list.map(up => (
                                    <div key={up.program_no}
                                        onClick={() => {
                                            // Use the FIRST machine that ran this program to load its current text.
                                            const firstMatch = programs.find(p => p.program_no === up.program_no);
                                            if (firstMatch) {
                                                loadFromMachine(firstMatch.device_id);
                                            } else if (up.machines.length > 0) {
                                                const machineMatch = programs.find(p => p.device_name === up.machines[0]);
                                                if (machineMatch) loadFromMachine(machineMatch.device_id);
                                            }
                                            showProgramHistory(up.program_no);
                                        }}
                                        style={{
                                            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                                            padding: 12, cursor: 'pointer',
                                            transition: 'box-shadow .15s, transform .15s, border-color .15s',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(124,58,237,0.12)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                            <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 800, color: '#7c3aed' }}>{up.program_no}</span>
                                            <span style={{ marginLeft: 'auto', fontSize: 10, padding: '2px 7px', background: '#f3f4f6', color: '#374151', borderRadius: 999, fontWeight: 700 }}>
                                                {up.times_loaded} load{up.times_loaded === 1 ? '' : 's'}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                                            {up.machines.map(m => (
                                                <span key={m} style={{
                                                    fontSize: 10, padding: '2px 6px', background: '#eef2ff', color: '#3730a3',
                                                    borderRadius: 4, fontWeight: 700, fontFamily: 'monospace',
                                                }}>{m}</span>
                                            ))}
                                        </div>
                                        <div style={{ fontSize: 10, color: '#9ca3af', display: 'flex', justifyContent: 'space-between' }}>
                                            <span>first: {fmtAgo(up.first_seen)}</span>
                                            <span>last: {fmtAgo(up.last_seen)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {programHistory && (
                            <div style={{ marginTop: 12, padding: 12, background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                    <span style={{ fontSize: 12, fontWeight: 800, color: '#92400e' }}>📜 Run history — {programHistory.programNo}</span>
                                    <span style={{ fontSize: 11, color: '#92400e' }}>{programHistory.events.length} events</span>
                                    <button onClick={() => setProgramHistory(null)} style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 11, fontWeight: 700, background: '#fff', color: '#92400e', border: '1px solid #fde68a', borderRadius: 4, cursor: 'pointer' }}>close</button>
                                </div>
                                <div style={{ maxHeight: 160, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {programHistory.events.map((ev, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11 }}>
                                            <span style={{ fontFamily: 'monospace', color: '#3730a3', fontWeight: 700, minWidth: 90 }}>{ev.device_name}</span>
                                            <span style={{ color: '#6b7280' }}>{new Date(ev.ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );})()}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: result ? 'minmax(380px, 1fr) minmax(0, 2fr)' : '1fr', gap: 16, alignItems: 'stretch' }}>
                {/* INPUT — stretches to match the right column's full height so user can
                    see code + 3D side-by-side. (Previous sticky-viewport behaviour is dropped
                    per request: matched heights matter more than scroll-following.) */}
                <div style={{ background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <SectionBadge n={1} title="G-Code Input" sub="program source" />
                        <div style={{ marginLeft: 'auto' }}>
                            <Chip>{result ? `PARSED · 0 ERRORS` : 'AWAITING INPUT'}</Chip>
                        </div>
                    </div>
                    {filename && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderTopLeftRadius: 6, borderTopRightRadius: 6, borderBottom: 'none', fontSize: 12, fontFamily: 'monospace', color: '#374151' }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#F3951D' }} />
                            <span style={{ fontWeight: 700 }}>{filename}</span>
                            {programNumber && (
                                <span style={{ marginLeft: 4, padding: '2px 8px', background: '#faf5ff', color: '#7c3aed', borderRadius: 4, fontSize: 11, fontWeight: 800, border: '1px solid #ddd6fe' }}>
                                    {programNumber}
                                </span>
                            )}
                        </div>
                    )}
                    <textarea
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="G90 G54 G17&#10;G0 X0 Y0 Z25&#10;G1 Z-1 F300&#10;...&#10;&#10;…or pick a machine above to load its latest program."
                        style={{
                            width: '100%', flex: 1, minHeight: 480, fontFamily: '"Fira Code", "Courier New", monospace', fontSize: 13,
                            padding: 12, border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 6, resize: 'vertical',
                            boxSizing: 'border-box', background: '#fbfaf7', color: '#1f2937', lineHeight: 1.55,
                        }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, fontSize: 10, color: '#9ca3af', fontFamily: 'monospace' }}>
                        <span>FILE {filename || 'untitled.nc'}</span>
                        <span>·</span>
                        <span>{code.split('\n').length} lines</span>
                        {result && (<><span>·</span><span>{result.segments} moves</span></>)}
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                            <label style={{ padding: '6px 12px', fontSize: 11, fontWeight: 700, background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #d1d5db)', borderRadius: 6, cursor: 'pointer', color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                ⬆ Upload program <span style={{ color: '#9ca3af', marginLeft: 4, fontWeight: 400 }}>.nc · .gcode · .txt</span>
                                <input type="file" accept=".nc,.gcode,.cnc,.txt" onChange={handleFile} style={{ display: 'none' }} />
                            </label>
                            <Button variant="contained" onClick={analyze}
                                sx={{ bgcolor: '#F3951D', '&:hover': { bgcolor: '#d97706' }, textTransform: 'none', fontWeight: 700, fontSize: 12 }}>
                                ⚡ Analyze
                            </Button>
                        </div>
                    </div>
                </div>

                {/* OUTPUT */}
                {result && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* TAB NAV — switches the right column between analysis / suggestions / output */}
                        <div style={{ background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 10, padding: '6px 8px', display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                            {[
                                { id: 'analysis', label: '📊 Analysis', sub: result ? `${result.segments} moves` : null, color: '#F3951D' },
                                { id: 'suggestions', label: 'Yantra Intelligence Suggestions', icon: 'yantra', sub: aiSuggestions ? `${aiSuggestions.length}` : null, color: '#1e40af' },
                                { id: 'output', label: '✨ Yantra Intelligence Output', sub: aiCode ? 'ready' : (aiCodeLoading ? '…' : null), color: '#7c3aed' },
                            ].map(t => (
                                <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                                    padding: '8px 16px', fontSize: 13, fontWeight: 700,
                                    background: activeTab === t.id ? t.color : '#fff',
                                    color: activeTab === t.id ? '#fff' : t.color,
                                    border: `1px solid ${t.color}`, borderRadius: 6, cursor: 'pointer',
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                }}>
                                    {t.icon === 'yantra' && <YantraIcon size={14} style={{ filter: activeTab === t.id ? 'brightness(0) invert(1)' : 'none' }} />}
                                    {t.label}
                                    {t.sub && (
                                        <span style={{
                                            fontSize: 10, fontWeight: 800, padding: '1px 7px', borderRadius: 999,
                                            background: activeTab === t.id ? 'rgba(255,255,255,0.25)' : t.color + '20',
                                            color: activeTab === t.id ? '#fff' : t.color,
                                        }}>{t.sub}</span>
                                    )}
                                </button>
                            ))}
                            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9ca3af', paddingRight: 8 }}>
                                {filename}{programNumber ? ' · ' : ''}{programNumber && <span style={{ color: '#7c3aed', fontWeight: 700, fontFamily: 'monospace' }}>{programNumber}</span>}
                            </span>
                        </div>

                        {activeTab === 'analysis' && (<>
                        {/* Component Reference (from Component Master) — only shown when machine is selected */}
                        {componentInfo?.master && (() => {
                            const m = componentInfo.master;
                            const base = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
                            const imgUrl = m.image_url ? `${base}${m.image_url}` : null;
                            const pdfUrl = m.diagram_url ? `${base}${m.diagram_url}` : null;
                            const isPdf = pdfUrl && pdfUrl.toLowerCase().endsWith('.pdf');
                            return (
                                <div style={{ background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 10, padding: 16 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                                        <div style={{ width: 26, height: 26, borderRadius: 6, background: '#0ea5e9', color: '#fff', fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📦</div>
                                        <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>Component Reference</div>
                                        <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>· from Component Master</div>
                                        <div style={{ marginLeft: 'auto' }}>
                                            <span style={{ fontSize: 11, padding: '3px 10px', background: '#ecfdf5', color: '#15803d', borderRadius: 999, fontWeight: 700, border: '1px solid #86efac' }}>
                                                Yantra Intelligence uses these for analysis
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '180px 180px 1fr', gap: 16, alignItems: 'start' }}>
                                        {/* Image */}
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                                            <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>Image</div>
                                            {imgUrl ? (
                                                <img src={imgUrl} alt="component"
                                                    onClick={() => setPopup({ url: imgUrl, isPdf: false, title: `Component image · ${m.component_name || ''}` })}
                                                    style={{ width: 170, height: 130, objectFit: 'contain', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 6, background: 'var(--y-bg-soft, #fafafa)', cursor: 'zoom-in' }} />
                                            ) : (
                                                <div style={{ width: 170, height: 130, border: '1px dashed #d1d5db', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 11 }}>No image</div>
                                            )}
                                        </div>
                                        {/* Diagram */}
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                                            <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>Diagram</div>
                                            {pdfUrl ? (
                                                isPdf ? (
                                                    <div onClick={() => setPopup({ url: pdfUrl, isPdf: true, title: `Component drawing · ${m.component_name || ''}` })}
                                                        style={{ width: 170, height: 130, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 6, background: '#fef3c7', color: '#92400e', cursor: 'zoom-in' }}>
                                                        <div style={{ fontSize: 32 }}>📄</div>
                                                        <div style={{ fontSize: 12, fontWeight: 800 }}>Click to view PDF</div>
                                                    </div>
                                                ) : (
                                                    <img src={pdfUrl} alt="diagram"
                                                        onClick={() => setPopup({ url: pdfUrl, isPdf: false, title: `Component diagram · ${m.component_name || ''}` })}
                                                        style={{ width: 170, height: 130, objectFit: 'contain', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 6, background: 'var(--y-bg-soft, #fafafa)', cursor: 'zoom-in' }} />
                                                )
                                            ) : (
                                                <div style={{ width: 170, height: 130, border: '1px dashed #d1d5db', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 11 }}>No diagram</div>
                                            )}
                                        </div>
                                        {/* Master details */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            <div>
                                                <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>Component Name</div>
                                                <div style={{ fontSize: 17, fontWeight: 800, color: '#111827', lineHeight: 1.2 }}>{m.component_name || '—'}</div>
                                                {m.component_number && (
                                                    <div style={{ fontSize: 12, fontFamily: 'monospace', color: '#7c3aed', fontWeight: 700, marginTop: 2 }}>#{m.component_number}</div>
                                                )}
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginTop: 4 }}>
                                                <div>
                                                    <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>Cycle time</div>
                                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#1e40af', fontFamily: 'monospace' }}>{m.cycle_time || '—'}</div>
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>Handling</div>
                                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#374151', fontFamily: 'monospace' }}>{m.handling_time || '—'}</div>
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>Setup</div>
                                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#374151', fontFamily: 'monospace' }}>{m.setup_time || '—'}</div>
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>Program No</div>
                                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#7c3aed', fontFamily: 'monospace' }}>{m.program_no || '—'}</div>
                                                </div>
                                                {m.cost_per_part != null && m.cost_per_part !== '' && (
                                                    <div>
                                                        <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>Cost / part</div>
                                                        <div style={{ fontSize: 15, fontWeight: 800, color: '#15803d' }}>₹ {Number(m.cost_per_part).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Stats */}
                        <div style={{ background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 10, padding: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                                <SectionBadge n={2} title="Program Stats" sub="static analysis" />
                                <div style={{ marginLeft: 'auto' }}>
                                    <Chip bg={result.rapidPct > 30 ? '#fee2e2' : '#dcfce7'} tx={result.rapidPct > 30 ? '#991b1b' : '#15803d'} dot={result.rapidPct > 30 ? '#dc2626' : '#22c55e'}>
                                        RAPID {result.rapidPct}% · {result.rapidPct > 30 ? 'HIGH' : 'HEALTHY'}
                                    </Chip>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                                <StatTile n={1} label="LINES" value={result.lines.toLocaleString()} />
                                <StatTile n={2} label="MOVES" value={result.segments.toLocaleString()} sub={`G00 · ${result.path.filter(p=>p.type==='G0').length}  ·  G01 · ${result.path.filter(p=>p.type==='G1').length}`} />
                                <StatTile n={3} label="TOOLS" value={result.toolCount} sub={result.toolCount === 0 ? '—' : `${result.tools.length} tools used`} />
                                <StatTile n={4} label="TOOL CHANGES" value={result.toolChanges} sub={result.toolChanges > 0 ? `${result.toolCount} unique` : '—'} />
                                <StatTile n={5} label="CUT DISTANCE" value={result.totalCut.toLocaleString() + ' mm'} sub={result.estTimeSec > 0 ? `~${Math.round(result.totalCut/1000)} m · feed` : '—'} color="#16a34a" />
                                <StatTile n={6} label="RAPID DISTANCE" value={result.totalRapid.toLocaleString() + ' mm'} sub={`${result.rapidPct}% of cycle`} color="#F3951D" />
                                <StatTile n={7} label="Z RANGE" value={`${Math.round(result.bbox.zMin)} → ${Math.round(result.bbox.zMax)}`} sub={`${Math.round(result.bbox.zMax - result.bbox.zMin)} mm envelope`} color="#1e40af" />
                                {(() => {
                                    const opnMatches = (code.match(/(?:^|\n)\s*\([^)]*OPN[^)]*\)/gi) || []).length;
                                    const setups = opnMatches >= 2 ? opnMatches : 1;
                                    const perSetup = Math.round(result.estTimeSec / setups);
                                    const showPerSetup = setups > 1;
                                    const subText = optimized
                                        ? `optimized: ${fmtTime(optimized.estTimeSec)}`
                                        : (showPerSetup
                                            ? `per setup · ${setups} setups · full program ${fmtTime(result.estTimeSec)}`
                                            : `cut ${fmtTime(result.estTimeSec - (result.toolChangeSec || 0) - Math.round((result.totalRapid || 0) / 30000 * 60))} + rapid ${fmtTime(Math.round((result.totalRapid || 0) / 30000 * 60))} + ATC ${result.toolChangeSec || 0}s`);
                                    return (
                                        <StatTile n={8}
                                            label={showPerSetup ? 'EST. CYCLE / PART' : 'EST. CYCLE'}
                                            value={fmtTime(showPerSetup ? perSetup : result.estTimeSec)}
                                            sub={subText}
                                            color="#1e40af" />
                                    );
                                })()}
                            </div>

                            {/* Multi-setup info — moved into the EST CYCLE tile sub-text already, no separate banner needed */}
                            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 120 }}>RAPID · CYCLE RATIO</div>
                                <div style={{ flex: 1, height: 8, background: 'var(--y-bg, #f3f4f6)', borderRadius: 4, overflow: 'hidden' }}>
                                    <div style={{ width: `${Math.min(100, result.rapidPct)}%`, height: '100%', background: result.rapidPct > 30 ? '#dc2626' : '#22c55e' }} />
                                </div>
                                <div style={{ fontSize: 11, fontWeight: 800, color: result.rapidPct > 30 ? '#991b1b' : '#15803d', minWidth: 90, textAlign: 'right' }}>
                                    {result.rapidPct}% · {result.rapidPct > 30 ? 'Above 30%' : 'Healthy'}
                                </div>
                            </div>
                        </div>

                        {/* 3D viz */}
                        <div style={{ background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 10, padding: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                                <SectionBadge n={3} title="3D Toolpath" sub="isometric" />
                                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', background: 'var(--y-bg-soft, #fafafa)', borderRadius: 6, border: '1px solid var(--y-border, #e5e7eb)' }}>
                                    <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>ANGLE</span>
                                    <input type="range" min="0" max="90" value={angle} onChange={(e) => setAngle(Number(e.target.value))}
                                        style={{ width: 110, accentColor: '#F3951D' }} />
                                    <span style={{ fontSize: 11, fontWeight: 800, color: '#111827', minWidth: 26 }}>{angle}°</span>
                                </div>
                            </div>
                            <Render3D
                                data={optimized ? result : result}
                                overlay={optimized}
                                height={340}
                                label={optimized
                                    ? `ORIGINAL (red dashed) vs OPTIMIZED (green) · overlay${programNumber ? ' · ' + programNumber : ''}`
                                    : `VIEW · ${filename || 'untitled.nc'}${programNumber ? ' · ' + programNumber : ''}`} />
                        </div>
                        </>)}

                        {activeTab === 'suggestions' && (
                        /* Single consolidated Yantra Intelligence button + Re-run option */
                        <div style={{ background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 10, padding: 14 }}>
                            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                                <Button
                                    onClick={runFullAiPipeline}
                                    disabled={aiLoading || aiCodeLoading}
                                    variant="contained"
                                    title={aiCode
                                        ? 'Yantra Intelligence output is ready in the Output tab. Click ↻ to re-run from scratch.'
                                        : 'One-click pipeline: Yantra Intelligence suggestions → instant rapid-travel cut → full Yantra Intelligence rewrite. Result lands in Yantra Intelligence Output tab.'}
                                    sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, textTransform: 'none', fontWeight: 700, fontSize: 14, px: 3, py: 1.2 }}
                                >
                                    {(aiLoading || aiCodeLoading) ? (
                                        <><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                                            {aiLoading ? 'Getting suggestions…' : `Yantra Intelligence rewriting (${estimateAiTime(code?.length)})…`}
                                        </>
                                    ) : aiCode ? (
                                        <>✓ Yantra Intelligence Optimization Complete — View Output</>
                                    ) : (
                                        <><YantraIcon size={16} style={{ marginRight: 6, filter: 'brightness(0) invert(1)' }} />✨ Run Yantra Intelligence Optimization</>
                                    )}
                                </Button>
                                {aiCode && !aiCodeLoading && !aiLoading && (
                                    <Button onClick={() => generateAiCode(true)} variant="outlined"
                                        title="Discard cached Yantra Intelligence output and re-run the full pipeline from scratch"
                                        sx={{ borderColor: '#7c3aed', color: '#7c3aed', textTransform: 'none', fontWeight: 700 }}>
                                        ↻ Re-run Yantra Intelligence
                                    </Button>
                                )}
                            </div>

                            {/* SLOWER OUTPUT WARNING — appears when Yantra Intelligence rewrite is slower than original */}
                            {aiCode && aiCode.timeDeltaSec != null && aiCode.timeDeltaSec < 0 && !aiCodeFromCache && (
                                <div style={{
                                    background: '#fef3c7', border: '1px solid #d97706', borderRadius: 8,
                                    padding: '10px 14px', marginBottom: 10,
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
                                }}>
                                    <div style={{ fontSize: 13, color: '#7a4a00', fontWeight: 600 }}>
                                        ⚠ Yantra Intelligence output is <b>{Math.abs(Math.round(aiCode.timeDeltaSec))}s slower</b> than the original — likely worth discarding and re-running.
                                    </div>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <Button size="small" variant="outlined"
                                            onClick={() => { setAiCode(null); clearAiCodeCache(code); }}
                                            sx={{ borderColor: '#d97706', color: '#7a4a00', textTransform: 'none', fontSize: 11, fontWeight: 700 }}>
                                            Discard
                                        </Button>
                                        <Button size="small" variant="contained"
                                            onClick={() => generateAiCode(true)}
                                            sx={{ bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' }, textTransform: 'none', fontSize: 11, fontWeight: 700 }}>
                                            ↻ Re-run
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {aiLoading && (
                                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: 12, fontSize: 12, color: '#1e40af', marginBottom: 10 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontWeight: 700 }}>
                                        <CircularProgress size={14} /> Yantra Intelligence is analyzing this program…
                                    </div>
                                    <div style={{ color: '#374151' }}>
                                        Sent to Yantra Intelligence: {result.lines} lines · {result.segments} moves · {result.toolCount} tools · {result.toolChanges} changes ·
                                        rapid {result.totalRapid} mm ({result.rapidPct}%) · cut {result.totalCut} mm · est. {fmtTime(result.estTimeSec)}.
                                    </div>
                                    <div style={{ color: '#6b7280', fontSize: 11, marginTop: 4 }}>Usually takes 5-15 seconds…</div>
                                </div>
                            )}

                            {aiErr && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{aiErr}</div>}

                            {/* 3D toolpath + rapid travel visualization — shown whenever
                                Yantra Intelligence suggestions are available so the supervisor can SEE
                                where the rapids/cuts are while reading the suggestions. */}
                            {aiSuggestions && aiSuggestions.length > 0 && result && (
                                <div style={{ background: 'var(--y-bg-soft, #fafafa)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>
                                            📐 Toolpath Preview
                                        </div>
                                        <span style={{ fontSize: 11, color: '#6b7280' }}>
                                            <span style={{ color: '#dc2626', fontWeight: 700 }}>—— rapids ({result.totalRapid?.toLocaleString() || 0} mm, {result.rapidPct}%)</span>
                                            {' · '}
                                            <span style={{ color: '#15803d', fontWeight: 700 }}>—— cuts ({result.totalCut?.toLocaleString() || 0} mm)</span>
                                            {' · '}
                                            {result.toolChanges} tool changes
                                        </span>
                                        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af' }}>
                                            Read the suggestions below to see what Yantra Intelligence thinks could be improved
                                        </span>
                                    </div>
                                    <Render3D
                                        data={result}
                                        label="Current program"
                                        height={300}
                                        width={760}
                                        progress={1}
                                        showCursor={false}
                                        compact
                                    />
                                </div>
                            )}

                            {aiSuggestions && aiSuggestions.length > 0 && (() => {
                                const parsed = aiSuggestions.map(s => ({ ...s, _t: parseSavings(s.savings_estimate) }));
                                const totalLo = parsed.reduce((a, s) => a + s._t.loSec, 0);
                                const totalHi = parsed.reduce((a, s) => a + s._t.hiSec, 0);
                                const cycle = result.estTimeSec || 1;
                                const pctLo = Math.round((totalLo / cycle) * 100);
                                const pctHi = Math.round((totalHi / cycle) * 100);
                                const sevColor = (sev) => sev === 'high' ? '#dc2626' : sev === 'low' ? '#3b82f6' : '#f59e0b';
                                const totalForBar = parsed.reduce((a, s) => a + (s._t.loSec + s._t.hiSec) / 2, 0) || 1;
                                return (
                                    <div style={{ marginTop: 4 }}>
                                        {/* === TOTAL SAVINGS SUMMARY (compact) === */}
                                        <div style={{ background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)', borderRadius: 8, padding: 12, marginBottom: 10, color: '#fff' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                                                <div>
                                                    <div style={{ fontSize: 10, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total potential savings</div>
                                                    <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.1, marginTop: 2 }}>
                                                        {totalLo > 0 ? `${fmtTime(totalLo)} – ${fmtTime(totalHi)}` : 'See details'}
                                                    </div>
                                                </div>
                                                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                                                    <div style={{ fontSize: 10, opacity: 0.85, textTransform: 'uppercase' }}>of cycle</div>
                                                    <div style={{ fontSize: 18, fontWeight: 900 }}>
                                                        {pctLo > 0 ? `${pctLo}-${pctHi}%` : '—'}
                                                    </div>
                                                    <div style={{ fontSize: 9, opacity: 0.75 }}>cycle: {fmtTime(cycle)}</div>
                                                </div>
                                            </div>
                                            {/* Stacked savings bar */}
                                            {totalLo > 0 && (
                                                <div>
                                                    <div style={{ display: 'flex', height: 22, borderRadius: 4, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.3)' }}>
                                                        {parsed.map((s, i) => {
                                                            const mid = (s._t.loSec + s._t.hiSec) / 2;
                                                            if (mid <= 0) return null;
                                                            const w = (mid / totalForBar) * 100;
                                                            return (
                                                                <div key={i} title={`${s.title}: ${s.savings_estimate}`}
                                                                    onClick={() => setExpandedSugg(expandedSugg === i ? null : i)}
                                                                    style={{
                                                                        width: w + '%', background: sevColor(s.severity),
                                                                        cursor: 'pointer',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        fontSize: 10, fontWeight: 700, color: '#fff',
                                                                        borderRight: i < parsed.length - 1 ? '1px solid rgba(255,255,255,0.3)' : 'none',
                                                                    }}>
                                                                    {w > 8 ? `#${i + 1}` : ''}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    <div style={{ fontSize: 10, opacity: 0.85, marginTop: 5 }}>
                                                        Each block = one suggestion's contribution. Click a block (or card below) to expand.
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div style={{ fontSize: 14, fontWeight: 800, color: '#374151', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <YantraIcon size={16} style={{ marginRight: 4 }} /> Yantra Intelligence Suggestions ({parsed.length})
                                            <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>· collapsed — click any row to see the detail + impact chart</span>
                                        </div>
                                        {parsed.map((s, i) => {
                                            const sev = String(s.severity || 'medium').toLowerCase();
                                            const colors = sev === 'high' ? { bg: '#fee2e2', bd: '#dc2626', tx: '#991b1b' }
                                                         : sev === 'low' ? { bg: '#eff6ff', bd: '#3b82f6', tx: '#1e40af' }
                                                                         : { bg: '#fef3c7', bd: '#f59e0b', tx: '#92400e' };
                                            const isOpen = expandedSugg === i;
                                            const mid = (s._t.loSec + s._t.hiSec) / 2;
                                            const pctOfCycle = cycle > 0 ? (mid / cycle) * 100 : 0;
                                            return (
                                                <div key={i}
                                                    onClick={() => setExpandedSugg(isOpen ? null : i)}
                                                    style={{ marginBottom: 6, padding: '8px 12px', background: colors.bg, border: '1px solid ' + colors.bd + (isOpen ? '' : '40'), borderRadius: 6, cursor: 'pointer', transition: 'border-color 0.15s' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                                        <div style={{ fontSize: 13, fontWeight: 800, color: colors.tx, flex: 1, minWidth: 200 }}>
                                                            {sev === 'high' ? '🔴' : sev === 'low' ? '🔵' : '🟡'} #{i + 1} {s.title}
                                                        </div>
                                                        {s.savings_estimate && (
                                                            <div style={{ fontSize: 11, color: '#15803d', fontWeight: 700, background: '#dcfce7', padding: '2px 8px', borderRadius: 999 }}>💰 {s.savings_estimate}</div>
                                                        )}
                                                        <div style={{ fontSize: 11, color: colors.tx, fontWeight: 700, minWidth: 70, textAlign: 'right' }}>{isOpen ? '▲ collapse' : '▼ expand'}</div>
                                                    </div>
                                                    {isOpen && (
                                                        <div style={{ fontSize: 13, color: '#374151', margin: '8px 0 4px', lineHeight: 1.55 }}>{s.detail}</div>
                                                    )}
                                                    {isOpen && s._t.hasTime && (
                                                        <div style={{ marginTop: 10, padding: 10, background: 'var(--y-bg, #fff)', borderRadius: 6, border: '1px solid var(--y-border, #e5e7eb)' }}>
                                                            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase' }}>
                                                                Impact on cycle time
                                                            </div>
                                                            {/* Mini bar: original cycle vs cycle minus this saving */}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                                <div style={{ fontSize: 10, color: '#6b7280', minWidth: 70 }}>Before</div>
                                                                <div style={{ flex: 1, height: 22, background: '#e5e7eb', borderRadius: 4, position: 'relative' }}>
                                                                    <div style={{ width: '100%', height: '100%', background: '#94a3b8', borderRadius: 4, display: 'flex', alignItems: 'center', paddingLeft: 8, color: '#fff', fontSize: 11, fontWeight: 700 }}>
                                                                        {fmtTime(cycle)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                                                <div style={{ fontSize: 10, color: '#6b7280', minWidth: 70 }}>After fix</div>
                                                                <div style={{ flex: 1, height: 22, background: '#e5e7eb', borderRadius: 4, position: 'relative', display: 'flex' }}>
                                                                    <div style={{ width: `${Math.max(0, 100 - pctOfCycle)}%`, height: '100%', background: '#16a34a', borderRadius: '4px 0 0 4px', display: 'flex', alignItems: 'center', paddingLeft: 8, color: '#fff', fontSize: 11, fontWeight: 700 }}>
                                                                        {fmtTime(Math.max(0, cycle - mid))}
                                                                    </div>
                                                                    <div style={{ width: `${pctOfCycle}%`, height: '100%', background: '#16a34a40', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#15803d' }}>
                                                                        {pctOfCycle > 4 ? `−${fmtTime(mid)}` : ''}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                                                                <div style={{ padding: 8, background: 'var(--y-bg-soft, #f9fafb)', borderRadius: 4 }}>
                                                                    <div style={{ fontSize: 10, color: '#6b7280' }}>Saves per cycle</div>
                                                                    <div style={{ fontSize: 14, fontWeight: 800, color: '#15803d' }}>{fmtTime(s._t.loSec)}–{fmtTime(s._t.hiSec)}</div>
                                                                </div>
                                                                <div style={{ padding: 8, background: 'var(--y-bg-soft, #f9fafb)', borderRadius: 4 }}>
                                                                    <div style={{ fontSize: 10, color: '#6b7280' }}>% of cycle</div>
                                                                    <div style={{ fontSize: 14, fontWeight: 800, color: '#1e40af' }}>{pctOfCycle.toFixed(1)}%</div>
                                                                </div>
                                                                <div style={{ padding: 8, background: 'var(--y-bg-soft, #f9fafb)', borderRadius: 4 }}>
                                                                    <div style={{ fontSize: 10, color: '#6b7280' }}>Per 100 parts</div>
                                                                    <div style={{ fontSize: 14, fontWeight: 800, color: '#15803d' }}>{fmtTime(mid * 100)}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {isOpen && !s._t.hasTime && (
                                                        <div style={{ marginTop: 10, padding: 10, background: 'var(--y-bg, #fff)', borderRadius: 6, border: '1px solid var(--y-border, #e5e7eb)', fontSize: 11, color: '#6b7280' }}>
                                                            ℹ️ Quality/tool-life improvement — no direct cycle-time impact, but improves part quality or tool wear.
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                            {aiSuggestions && aiSuggestions.length === 0 && !aiLoading && (
                                <div style={{ background: '#dcfce7', color: '#15803d', padding: 10, borderRadius: 6, fontSize: 12 }}>
                                    ✅ Yantra Intelligence found no significant optimizations — program looks well-tuned.
                                </div>
                            )}
                            {!aiSuggestions && !aiLoading && !aiErr && (
                                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af', fontSize: 13, border: '1px dashed #e5e7eb', borderRadius: 6 }}>
                                    Click <strong>Get Yantra Intelligence Suggestions</strong> above to have Yantra Intelligence analyze this program.
                                </div>
                            )}
                        </div>
                        )}

                        {activeTab === 'output' && (<>
                        {!aiCode && !aiCodeLoading && !aiCodeErr && (
                            <div style={{ background: 'var(--y-bg, #fff)', border: '1px dashed #e5e7eb', borderRadius: 10, padding: '40px 20px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                                <div style={{ fontSize: 24, marginBottom: 8 }}>✨</div>
                                <div style={{ marginBottom: 6 }}>No Yantra Intelligence output yet.</div>
                                <div>Go to the <strong>Yantra Intelligence Suggestions</strong> tab and click <strong>✨ Generate Optimized G-Code (Yantra Intelligence)</strong>.</div>
                            </div>
                        )}
                        {/* === Yantra Intelligence-GENERATED OPTIMIZED G-CODE === */}
                        {(aiCodeLoading || aiCode || aiCodeErr) && (
                            <div style={{ background: 'var(--y-bg, #fff)', border: '2px solid #7c3aed', borderRadius: 10, padding: 16 }}>
                                <div style={{ fontSize: 16, fontWeight: 800, color: '#7c3aed', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    ✨ Yantra Intelligence-Generated Optimized G-Code
                                    {aiCodeFromCache && aiCode?.cachedAt && (
                                        <span title={`${aiCode.serverCached ? 'Restored from Component Master (server cache) — shared across all users/devices' : 'Restored from local browser cache'} · originally generated ${new Date(aiCode.cachedAt).toLocaleString()}`}
                                            style={{ fontSize: 11, fontWeight: 800, background: '#ecfdf5', color: '#15803d', padding: '3px 10px', borderRadius: 999, border: '1px solid #86efac' }}>
                                            ⚡ {aiCode.serverCached ? 'from Component Master' : 'from local cache'} · saved {fmtAgo(aiCode.cachedAt)}
                                        </span>
                                    )}
                                    {aiCode && (
                                        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                            <button onClick={copyAiCode} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, background: 'var(--y-bg, #fff)', border: '1px solid #7c3aed', color: '#7c3aed', borderRadius: 4, cursor: 'pointer' }}>📋 Copy</button>
                                            <button onClick={downloadAiCode} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, background: '#7c3aed', border: '1px solid #7c3aed', color: '#fff', borderRadius: 4, cursor: 'pointer' }}>⬇ Download .nc</button>
                                            <button onClick={() => generateAiCode(true)} title="Force a fresh Yantra Intelligence run, ignoring cache" style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #d1d5db)', color: '#6b7280', borderRadius: 4, cursor: 'pointer' }}>↻ Re-run</button>
                                        </span>
                                    )}
                                </div>
                                {aiCodeLoading && (
                                    <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6, padding: 12, fontSize: 12, color: '#5b21b6' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontWeight: 700 }}>
                                            <CircularProgress size={14} sx={{ color: '#7c3aed' }} /> Yantra Intelligence is rewriting your G-code…
                                        </div>
                                        <div style={{ color: '#6b7280' }}>Sending {code.length.toLocaleString()} chars · applying {aiSuggestions?.length || 0} suggestions · estimated <b>{estimateAiTime(code.length)}</b>.</div>
                                    </div>
                                )}
                                {aiCodeErr && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 6, fontSize: 12 }}>{aiCodeErr}</div>}
                                {aiCode && (() => {
                                    // Two different numbers — show both honestly side-by-side:
                                    //   PROJECTED = sum of Yantra Intelligence's per-suggestion estimates (what Yantra Intelligence thinks COULD be saved)
                                    //   MEASURED  = re-parse the Yantra Intelligence-rewritten code, compute the actual cycle-time delta
                                    // They often differ because the Yantra Intelligence may not have applied every suggestion,
                                    // or suggestions may be optimistic. Showing both prevents the user from
                                    // mistaking the projection for a guaranteed saving.
                                    const suggParsed = (aiSuggestions || []).map(s => parseSavings(s.savings_estimate));
                                    const suggLoSec = suggParsed.reduce((a, s) => a + s.loSec, 0);
                                    const suggHiSec = suggParsed.reduce((a, s) => a + s.hiSec, 0);
                                    const parserDelta = aiCode.timeDeltaSec;
                                    const isParserSuspect = parserDelta > Math.max(60, suggHiSec * 2);
                                    const underdelivered = suggLoSec > 0 && parserDelta < suggLoSec * 0.5 && !isParserSuspect;
                                    return (
                                    <>
                                        {/* Honest dual-savings banner: Yantra Intelligence projection vs measured */}
                                        {(suggLoSec > 0 || isParserSuspect) && (
                                            <div style={{ background: 'var(--y-bg-soft, #f9fafb)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                    {suggLoSec > 0 && (
                                                        <div>
                                                            <div style={{ fontSize: 11, fontWeight: 800, color: '#1e40af', textTransform: 'uppercase', letterSpacing: 0.5 }} title="Sum of every Yantra Intelligence suggestion's own time-saving estimate. This is what the Yantra Intelligence THINKS could be saved if all suggestions are applied — a projection, not a measurement.">
                                                                Yantra Intelligence projected savings (sum of suggestion estimates)
                                                            </div>
                                                            <div style={{ fontSize: 18, fontWeight: 800, color: '#1e40af', marginTop: 2 }}>
                                                                {fmtTime(suggLoSec)} – {fmtTime(suggHiSec)} <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>per cycle</span>
                                                            </div>
                                                            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>theoretical · what the Yantra Intelligence claimed</div>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div style={{ fontSize: 11, fontWeight: 800, color: parserDelta > 0 ? '#15803d' : '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }} title="Actual cycle-time difference computed by re-parsing the Yantra Intelligence-rewritten code. This is what the program WILL run as.">
                                                            Measured savings (this rewrite, parser)
                                                        </div>
                                                        <div style={{ fontSize: 18, fontWeight: 800, color: parserDelta > 0 ? '#15803d' : '#9ca3af', marginTop: 2 }}>
                                                            {parserDelta > 0 ? `${fmtTime(parserDelta)} per cycle` : 'no measurable cycle change'}
                                                        </div>
                                                        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>actual · re-parsed from Yantra Intelligence output</div>
                                                    </div>
                                                </div>
                                                {underdelivered && (
                                                    <div style={{ marginTop: 10, fontSize: 12, color: '#92400e', lineHeight: 1.5, padding: '6px 10px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 4 }}>
                                                        ⚠ Yantra Intelligence under-delivered: it projected {fmtTime(suggLoSec)}+ savings but the rewrite only measures {fmtTime(parserDelta)}. Likely the Yantra Intelligence applied a subset of suggestions (formatting / structure changes that don't affect cycle time). Use <strong>↻ Re-run</strong> to try again, or apply the high-impact suggestions manually.
                                                    </div>
                                                )}
                                                <details style={{ marginTop: 10, fontSize: 12, color: '#374151' }}>
                                                    <summary style={{ cursor: 'pointer', fontWeight: 700, color: '#7c3aed', userSelect: 'none' }}>
                                                        🤔 Why don't the two numbers match? (click to expand)
                                                    </summary>
                                                    <div style={{ marginTop: 8, padding: '10px 12px', background: '#faf5ff', border: '1px solid #ddd6fe', borderRadius: 6, lineHeight: 1.6 }}>
                                                        <div style={{ marginBottom: 8 }}>
                                                            They measure <strong>different things</strong> — they're rarely equal, and that's expected:
                                                        </div>
                                                        <div style={{ marginBottom: 6 }}>
                                                            <span style={{ color: '#1e40af', fontWeight: 700 }}>🟦 Yantra Intelligence projected</span> = sum of Yantra Intelligence's per-suggestion estimates. <em>Theoretical maximum</em> assuming every suggestion is fully applied AND the parser counts all the savings.
                                                        </div>
                                                        <div style={{ marginBottom: 6 }}>
                                                            <span style={{ color: '#15803d', fontWeight: 700 }}>🟩 Parser measured</span> = actual cycle delta the parser can detect. Only counts <strong>feed/rapid moves × distance ÷ speed</strong>.
                                                        </div>
                                                        <div style={{ marginTop: 8, marginBottom: 4, fontWeight: 700 }}>The parser is blind to (these explain most of the gap):</div>
                                                        <ul style={{ margin: '4px 0 8px 20px', padding: 0 }}>
                                                            <li><strong>Tool changes</strong> — every M06 takes 6-10s but parser counts 0s</li>
                                                            <li><strong>M-code overhead</strong> — M00/M01/M75/M76 (probes, dwells, manual stops) cost real seconds</li>
                                                            <li><strong>Spindle ramp-up</strong> — M03 → reaching target RPM takes 1-3s</li>
                                                            <li><strong>Macro savings</strong> — when Yantra Intelligence optimizes a macro called 50× the parser may only see 1 call</li>
                                                            <li><strong>Operator wait time</strong> — manual interventions removed but parser doesn't track them</li>
                                                        </ul>
                                                        <div style={{ marginTop: 6, color: '#6b7280' }}>
                                                            👉 <strong>Trust the measured number for short-cycle parts</strong> (small programs).<br />
                                                            👉 <strong>Trust the Yantra Intelligence projection for tool-change-heavy programs</strong> (many M06s, multi-tool setups) — savings are real even if parser doesn't see them.
                                                        </div>
                                                    </div>
                                                </details>
                                                {isParserSuspect && (
                                                    <div style={{ marginTop: 10, fontSize: 12, color: '#92400e', lineHeight: 1.5, padding: '6px 10px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 4 }}>
                                                        ⚠ Measured number is suspect — parser claims {fmtTime(parserDelta)} but Yantra Intelligence's max projection was {fmtTime(suggHiSec)}. Likely a macro/loop the parser can't follow. Trust the Yantra Intelligence projection over the measured number in this case.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {/* Before/after stat row (parser-computed) */}
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
                                            <DeltaTile
                                                label={isParserSuspect ? '⚠ Est cycle (parser)' : 'Est cycle'}
                                                before={fmtTime(result.estTimeSec)}
                                                after={fmtTime(aiCode.stats.estTimeSec)}
                                                delta={parserDelta !== 0
                                                    ? (parserDelta > 0 ? `−${fmtTime(parserDelta)} saved` : `+${fmtTime(Math.abs(parserDelta))} slower`)
                                                    : 'no change'}
                                                good={parserDelta > 0 ? true : (parserDelta < 0 ? false : null)}
                                            />
                                            <DeltaTile
                                                label="Rapid dist"
                                                before={`${result.totalRapid} mm`}
                                                after={`${aiCode.stats.totalRapid} mm`}
                                                delta={aiCode.rapidDeltaMm !== 0
                                                    ? (aiCode.rapidDeltaMm > 0 ? `−${aiCode.rapidDeltaMm} mm` : `+${Math.abs(aiCode.rapidDeltaMm)} mm`)
                                                    : 'no change'}
                                                good={aiCode.rapidDeltaMm > 0 ? true : (aiCode.rapidDeltaMm < 0 ? false : null)}
                                            />
                                            <DeltaTile
                                                label="Tool changes"
                                                before={result.toolChanges}
                                                after={aiCode.stats.toolChanges}
                                                delta={aiCode.toolChangeDelta !== 0
                                                    ? (aiCode.toolChangeDelta > 0 ? `−${aiCode.toolChangeDelta}` : `+${Math.abs(aiCode.toolChangeDelta)}`)
                                                    : 'no change'}
                                                good={aiCode.toolChangeDelta > 0 ? true : (aiCode.toolChangeDelta < 0 ? false : null)}
                                            />
                                            <DeltaTile
                                                label="Lines"
                                                before={result.lines}
                                                after={aiCode.stats.lines}
                                                delta={aiCode.lineDelta !== 0 ? (aiCode.lineDelta > 0 ? `+${aiCode.lineDelta}` : `${aiCode.lineDelta}`) : 'no change'}
                                                good={null}
                                            />
                                        </div>
                                        {/* BEFORE vs AFTER toolpath animation (NC viewer style) */}
                                        <NcPlayer before={result} after={aiCode.stats} originalText={code} optimizedText={aiCode.text} />

                                        {/* Code diff — original vs Yantra Intelligence-optimized */}
                                        <DiffView original={code} optimized={aiCode.text} />
                                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                                            💡 <strong>Side-by-side</strong>: green = added by Yantra Intelligence, red = removed, yellow = modified line. <strong>Unified</strong>: single column with +/− markers. <strong>Raw optimized</strong>: copy-friendly full text. Look for parens-comments like <code style={{ background: 'var(--y-bg, #f3f4f6)', padding: '1px 4px', borderRadius: 3 }}>(FEED 90→200)</code> or <code style={{ background: 'var(--y-bg, #f3f4f6)', padding: '1px 4px', borderRadius: 3 }}>(REGROUPED BY TOOL)</code> for the Yantra Intelligence's reasoning. <strong>Always dry-run + simulate before machining.</strong>
                                        </div>
                                    </>
                                    );
                                })()}
                            </div>
                        )}

                        {optimized && (
                            <div style={{ background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 10, padding: 14 }}>
                                <div style={{ fontSize: 14, fontWeight: 800, color: '#111827', marginBottom: 10 }}>⚡ Algorithmic Optimization Result</div>
                                <div style={{ padding: 12, background: optimized.savedSec > 0 ? '#dcfce7' : '#fef3c7', borderRadius: 6, marginBottom: 12 }}>
                                    {optimized.savedSec > 0 ? (
                                        <div>
                                            <div style={{ fontSize: 18, fontWeight: 800, color: '#15803d', marginBottom: 4 }}>
                                                💰 Saved {fmtTime(optimized.savedSec)} ({optimized.savedPct}%)
                                            </div>
                                            <div style={{ fontSize: 13, color: '#374151' }}>
                                                Rapid travel cut by <strong>{optimized.savedRapidMm} mm</strong>. {optimized.groupsReordered} cut groups reordered using nearest-neighbor algorithm. (Switch to <strong>📊 Analysis</strong> tab to see the overlay on the 3D view.)
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: 13, color: '#92400e' }}>
                                            ℹ️ Already well-ordered — nearest-neighbor produced no improvement.
                                        </div>
                                    )}
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                                    Always validate optimized G-code in a simulator before running on a real machine.
                                </div>
                            </div>
                        )}
                        </>)}
                    </div>
                )}
            </div>

            {/* Full-screen popup modal for image / PDF preview — replaces the
                previous "open in new tab" behaviour per request. Click outside
                or the × button to close. */}
            {popup && (
                <div onClick={() => setPopup(null)}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
                        zIndex: 9999, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', padding: 24,
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, color: '#fff', fontSize: 14, fontWeight: 700, alignSelf: 'stretch' }}>
                        <span>{popup.title}</span>
                        <button onClick={(e) => { e.stopPropagation(); setPopup(null); }}
                            style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: 13, fontWeight: 700, background: 'var(--y-bg, #fff)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>× Close</button>
                    </div>
                    <div onClick={(e) => e.stopPropagation()} style={{ flex: 1, width: '100%', maxWidth: 1400, background: 'var(--y-bg, #fff)', borderRadius: 6, overflow: 'hidden' }}>
                        {popup.isPdf ? (
                            <iframe src={popup.url} title={popup.title}
                                style={{ width: '100%', height: '100%', border: 'none' }} />
                        ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--y-bg-soft, #fafafa)' }}>
                                <img src={popup.url} alt={popup.title}
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            </div>
                        )}
                    </div>
                    <div style={{ color: '#d1d5db', fontSize: 12, marginTop: 8 }}>
                        Press <strong>Esc</strong> or click outside to close
                    </div>
                </div>
            )}
        </div>
    );
};

const Stat = ({ label, value, color = '#111827' }) => (
    <div style={{ padding: 10, background: 'var(--y-bg-soft, #f9fafb)', borderRadius: 6 }}>
        <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 800, color, marginTop: 2 }}>{value}</div>
    </div>
);

// New: numbered stat tile matching the mockup design
const StatTile = ({ n, label, value, sub, color = '#111827' }) => (
    <div style={{ padding: 14, background: 'var(--y-bg-soft, #fafafa)', borderRadius: 8, border: '1px solid var(--y-border, #e5e7eb)', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
            <div style={{ fontSize: 10, color: '#d1d5db', fontFamily: 'monospace' }}>/{String(n).padStart(2, '0')}</div>
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1.1, letterSpacing: -0.3 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, fontFamily: 'monospace' }}>{sub}</div>}
    </div>
);

const DeltaTile = ({ label, before, after, delta, good }) => (
    <div style={{ padding: 12, background: 'var(--y-bg-soft, #f9fafb)', borderRadius: 8, border: '1px solid var(--y-border, #e5e7eb)' }}>
        <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 800, letterSpacing: 0.6, marginBottom: 6 }}>{label}</div>
        <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 2 }}>before <span style={{ color: '#374151', fontWeight: 700 }}>{before}</span></div>
        <div style={{ fontSize: 13, color: '#9ca3af' }}>after <span style={{ color: '#111827', fontWeight: 800 }}>{after}</span></div>
        {delta && <div style={{ fontSize: 16, fontWeight: 800, marginTop: 6, color: good === false ? '#dc2626' : good === true ? '#15803d' : '#6b7280' }}>{delta}</div>}
    </div>
);

export default GcodeOptimizer;
